<template>
  <div class="flow-renderer">
    <div
      ref="canvasWrapper"
      class="flow-renderer__canvas-wrapper"
      @pointerdown="onPointerDown"
      @wheel.prevent="onWheel"
    >
      <div
        v-if="nodes.length"
        class="flow-renderer__canvas"
        :style="canvasTransform"
      >
        <svg
          class="flow-renderer__connections"
          :width="canvasSize.width"
          :height="canvasSize.height"
        >
          <g class="flow-renderer__connection-trunks">
            <path
              v-for="trunk in trunks"
              :key="trunk.id"
              :d="trunk.path"
              :class="{
                'flow-renderer__connection-branch--error':
                  trunk.warningSeverity === 'error',
                'flow-renderer__connection-branch--warning':
                  trunk.warningSeverity === 'warning',
                'flow-renderer__connection-branch--info':
                  trunk.warningSeverity === 'info',
                'flow-renderer__connection-branch--hovered':
                  !isSelectionModeActive &&
                  !isBranchOperationActive &&
                  trunk.parentId &&
                  (hoveredWarningParentId === trunk.parentId ||
                    isRelatedToHoveredNode(trunk.parentId)),
                'flow-renderer__connection-branch--traversed':
                  isStepTraversed(trunk.parentId)
              }"
            />
          </g>
          <g class="flow-renderer__connection-branches">
            <path
              v-for="branch in branches"
              :key="branch.id"
              :d="branch.path"
              :class="{
                'flow-renderer__connection-branch--error':
                  branch.warningSeverity === 'error',
                'flow-renderer__connection-branch--warning':
                  branch.warningSeverity === 'warning',
                'flow-renderer__connection-branch--info':
                  branch.warningSeverity === 'info',
                'flow-renderer__connection-branch--hovered':
                  !isSelectionModeActive &&
                  !isBranchOperationActive &&
                  ((hoveredWarningParentId &&
                    branch.parentId === hoveredWarningParentId) ||
                    (branch.parentId &&
                      isRelatedToHoveredNode(branch.parentId)) ||
                    (hoveredRemovableConnection &&
                      branch.parentId === hoveredRemovableConnection.parentId &&
                      branch.childId === hoveredRemovableConnection.childId)),
                'flow-renderer__connection-branch--removable':
                  branch.canRemove && !isSelectionModeActive && !isBranchOperationActive,
                'flow-renderer__connection-branch--traversed':
                  isStepTraversed(branch.parentId) && isStepTraversed(branch.childId)
              }"
            />
            <path
              v-for="branch in freeOutputBranches"
              :key="branch.id"
              :d="branch.path"
            />
          </g>
          <g class="flow-renderer__end-marker-links">
            <path
              v-for="link in endMarkerLinks"
              :key="link.id"
              :d="link.path"
            />
          </g>
        </svg>
        <!-- Branch warning icons (HTML elements for proper FontAwesome rendering) -->
        <div
          v-for="warning in branchWarnings"
          :key="warning.id"
          class="flow-renderer__branch-warning"
          :class="[
            `flow-renderer__branch-warning--${warning.severity}`,
            { 'flow-renderer__branch-warning--dimmed': isSelectionModeActive }
          ]"
          :style="{ top: `${warning.y}px`, left: `${warning.x}px` }"
          @mouseenter="hoveredWarningParentId = warning.parentId"
          @mouseleave="hoveredWarningParentId = null"
        >
          <i class="fas fa-exclamation-triangle" />
          <div
            class="flow-renderer__branch-warning-tooltip"
            :class="{
              'flow-renderer__branch-warning-tooltip--single':
                warning.messages.length === 1
            }"
          >
            <span v-for="(message, index) in warning.messages" :key="index">{{
              t(message)
            }}</span>
          </div>
        </div>
        <!-- Output labels for filters (O/N) -->
        <div
          v-for="label in outputLabels"
          :key="label.id"
          class="flow-renderer__output-label"
          :class="{
            'flow-renderer__output-label--dimmed': isSelectionModeActive
          }"
          :style="{
            top: `${label.y}px`,
            left: `${label.x}px`,
            color: label.color
          }"
        >
          {{ label.label }}
        </div>

        <!-- Swap Buttons -->
        <template v-if="!isSelectionModeActive && !flowAnalytics.isAnalyticsMode.value">
          <FlowSwapButton
            v-for="btn in swapButtons"
            :key="btn.stepId"
            :style="{ left: `${btn.x}px`, top: `${btn.y}px` }"
            @click="onSwapBranches(btn.stepId)"
            v-tooltip.top="t('flows.branchReorder.title')"
          />
        </template>

        <!-- Removable branch hitareas (double-click to remove connection) -->
        <div
          v-for="hitarea in removableBranchHitareas"
          v-show="!isSelectionModeActive && !isBranchOperationActive && !flowAnalytics.isAnalyticsMode.value"
          :key="hitarea.id"
          class="flow-renderer__branch-hitarea"
          :style="{
            left: `${hitarea.x}px`,
            top: `${hitarea.y}px`,
            width: `${hitarea.width}px`,
            height: `${hitarea.height}px`
          }"
          @mouseenter="hoveredRemovableConnection = { parentId: hitarea.parentId, childId: hitarea.childId }"
          @mouseleave="hoveredRemovableConnection = null"
          @dblclick.stop.prevent="onHitareaDoubleClick(hitarea)"
        />

        <div
          class="flow-renderer__nodes"
          :style="{
            width: `${canvasSize.width}px`,
            height: `${canvasSize.height}px`
          }"
        >
          <FlowNode
            v-for="node in nodes"
            :key="node.step.id"
            :node="node"
            :flow="flow"
            :width="BOX_WIDTH"
            :height="BOX_HEIGHT"
            :target-calculation="getTargetCalculation(node.step.id)"
            :selection-mode="isSelectionModeActive || isBranchOperationActive"
            :is-selectable="
              (isSelectionModeActive &&
                isStepSelectable(node.step.type, node.step.id)) ||
                isMergeTargetSelectable(node.step.id)
            "
            :is-selected="
              (isSelectionModeActive && isStepSelected(node.step.id)) ||
                isMergeTargetSelected(node.step.id)
            "
            :is-highlighted="isStepHighlighted(node.step.id)"
            :is-merge-target="
              branchOps.selectionState.value.mode === 'merge-select'
            "
            :is-loading="loadingStepIds.has(node.step.id)"
            @add-before="onAddBefore(node)"
            @add-after="onAddAfter(node)"
            @click="onNodeClick(node)"
            @delete="onNodeDelete(node)"
            @replace="onNodeReplace(node)"
            @menu-action="onMenuAction"
            @warning-action="handleWarningAction"
            @toggle-enabled="onToggleEnabled"
            @selection-toggle="onNodeSelectionToggle(node.step.id)"
            @mouseenter="onNodeMouseEnter(node.step.id)"
            @mouseleave="onNodeMouseLeave"
            @resize="handleNodeResize"
          />
          <FlowAddButton
            v-for="btn in addButtons"
            v-show="!flowAnalytics.isAnalyticsMode.value"
            :key="btn.id"
            :top="btn.top"
            :left="btn.left"
            :closure-mode="
              branchOps.selectionState.value.mode === 'closure-select'
            "
            :closure-selectable="
              isClosureOutputSelectable(btn.parentStepId, btn.outputIndex)
            "
            :closure-selected="
              branchOps.isOutputSelectedForClosure(
                btn.parentStepId,
                btn.outputIndex
              )
            "
            @click="onAddButtonClick(btn)"
          />
          <!-- End-of-branch markers -->
          <div
            v-for="marker in endMarkers"
            v-show="!isSelectionModeActive && !isBranchOperationActive"
            :key="marker.id"
            class="flow-renderer__end-marker"
            :style="{ top: `${marker.top}px`, left: `${marker.left}px` }"
          >
            <i class="fas fa-flag-checkered" />
            <div class="flow-renderer__end-marker-tooltip">
              {{ t('flows.endMarker') }}
            </div>
          </div>
          <!-- Branch menu (add/merge/closure) -->
          <FlowBranchMenu
            :visible="branchMenuVisible"
            :position="branchMenuPosition"
            :show-merge="branchMenuShowMerge"
            :show-closure="branchMenuShowClosure"
            @action="onBranchMenuAction"
          />
        </div>
      </div>
      <FlowEmptyState v-else :type="type" @start-flow="onEmptyStateStart" />
    </div>

    <Transition name="flow-renderer__minimap-fade">
      <button
        v-if="nodes.length && minimapCollapsed"
        type="button"
        class="flow-renderer__minimap-pill"
        :title="t('flows.minimap.expand')"
        @click="setMinimapCollapsed(false)"
      >
        <i class="far fa-map" />
      </button>
      <div
        v-else-if="nodes.length"
        class="flow-renderer__minimap"
      >
        <button
          type="button"
          class="flow-renderer__minimap-collapse"
          :title="t('flows.minimap.collapse')"
          :aria-label="t('flows.minimap.collapse')"
          @click.stop="setMinimapCollapsed(true)"
        >
          <i class="far fa-times" />
        </button>
        <svg
          :width="minimapSize.width"
          :height="minimapSize.height"
          class="flow-renderer__minimap-svg"
          @click="onMiniMapClick"
        >
          <g>
            <rect
              v-for="node in nodes"
              :key="`minimap-${node.step.id}`"
              class="flow-renderer__minimap-node"
              :x="node.left * minimapScale"
              :y="node.top * minimapScale"
              :width="BOX_WIDTH * minimapScale"
              :height="BOX_HEIGHT * minimapScale"
              :class="`flow-renderer__minimap-node--${node.kind}`"
            />
          </g>
          <rect
            class="flow-renderer__minimap-viewport"
            :x="visibleRect.left * minimapScale"
            :y="visibleRect.top * minimapScale"
            :width="visibleRect.width * minimapScale"
            :height="visibleRect.height * minimapScale"
          />
        </svg>
      </div>
    </Transition>

    <FlowOperatorPanel
      v-model:visible="operatorPanelVisible"
      :context="operatorPanelContext"
      :edit-step="selectedStep"
      :replace-step="replaceStep"
      :flow="flow"
      @add="onOperatorAdd"
      @save="onStepSave"
      @replace="onOperatorReplace"
      @decrease-branches="onDecreaseBranches"
      @update-step="onPanelUpdateStep"
    />

    <!-- Selection mode floating bar -->
    <Transition name="slide-up">
      <div v-if="isSelectionModeActive" class="flow-renderer__selection-bar">
        <div class="flow-renderer__selection-bar-content">
          <span class="flow-renderer__selection-bar-text">
            {{ t("automatedScenarios.filters.linkedOperators.label") }}
          </span>
          <div class="flow-renderer__selection-bar-actions">
            <Button
              :label="
                t('automatedScenarios.filters.linkedOperators.cancel_selection')
              "
              class="p-button-text"
              @click="cancelSelection"
            />
            <Button
              :label="
                t(
                  'automatedScenarios.filters.linkedOperators.confirm_selection'
                )
              "
              class="p-button-success"
              @click="confirmSelection"
            />
          </div>
        </div>
      </div>
    </Transition>

    <!-- Branch operations floating bar (merge/closure mode) -->
    <Transition name="slide-up">
      <div
        v-if="branchOps.selectionState.value.mode !== 'normal'"
        class="flow-renderer__selection-bar"
      >
        <div class="flow-renderer__selection-bar-content">
          <span class="flow-renderer__selection-bar-text">
            {{
              branchOps.selectionState.value.mode === "merge-select"
                ? t("flows.branchOperations.mergeInfo")
                : t("flows.branchOperations.closureInfo")
            }}
          </span>
          <div class="flow-renderer__selection-bar-actions">
            <Button
              :label="t('flows.branchOperations.cancel')"
              class="p-button-text"
              @click="branchOps.cancel()"
            />
            <Button
              v-if="branchOps.selectionState.value.mode === 'merge-select'"
              :label="t('flows.branchOperations.validate')"
              class="p-button-success"
              :disabled="
                !branchOps.selectionState.value.mergeData?.targetStepId
              "
              @click="onMergeValidate"
            />
            <Button
              v-else
              :label="t('flows.branchOperations.validate')"
              class="p-button-success"
              :disabled="!branchOps.canCompleteClosure.value"
              @click="onClosureValidate"
            />
          </div>
        </div>
      </div>
    </Transition>

    <FlowBranchReorderModal
      v-model:visible="reorderModalVisible"
      :step-id="reorderStepId || ''"
      :flow="flow"
      @save="onReorderSave"
      @close="reorderModalVisible = false"
    />
  </div>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  nextTick,
  onBeforeUnmount,
  onMounted,
  PropType,
  reactive,
  ref,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { getOperatorById, OperatorInsertContext, calculatePersistenceForStep } from '@/components/flows/operators/registry';
import type { FlowOperatorMetadata, NodeWarning, CampaignType, BranchMenuAction, FlowEditorMode } from '@/types/flow-types';
import { TypeCampaignEnum } from '@/types';
import { useNodeWarnings, getTemplateAndRedirect } from '@/composables/flows';
import { useBranchOperations } from '@/composables/flows/useBranchOperations';
import { useTargetBulkCalculations } from '@/composables/flows/useTargetBulkCalculations';
import { useLinkedOperatorSelection, forceResetSelection } from '@/composables/flows/useLinkedOperatorSelection';
import { useLinkedOperatorHighlight } from '@/composables/flows/useLinkedOperatorHighlight';
import { useWhatsappPollingManager, WhatsappValidationStatus, isBlockingStatus as isBlockingWhatsappStatus, isPendingStatus as isPendingWhatsappStatus } from '@/composables/flows/useWhatsappTemplateValidation';
import { useGlobalFlowAnalytics } from '@/composables/flows/useFlowAnalytics';
import { UserState } from '@/composables/User';
import { parseOperatorParameters } from '@/helpers/flowParameters';
import Button from 'primevue/button';
import FlowNode from '@/components/flows/FlowNode.vue';
import FlowAddButton from '@/components/flows/FlowAddButton.vue';
import FlowOperatorPanel from '@/components/flows/FlowOperatorPanel.vue';
import FlowEmptyState from '@/components/flows/FlowEmptyState.vue';
import FlowBranchMenu from '@/components/flows/FlowBranchMenu.vue';
import FlowBranchReorderModal from '@/components/flows/FlowBranchReorderModal.vue';
import FlowSwapButton from '@/components/flows/FlowSwapButton.vue';
import { FlowStructureDto, FlowStructureType, FlowStepDto } from '@/types/flow-structure';

interface LayoutNode {
  step: FlowStructureDto['steps'][number];
  label: string;
  subtitle: string;
  icon: string;
  kind: string;
  left: number;
  top: number;
  centerX: number;
  hasInput: boolean;
  outputs: number;
  connectedOutputs: number[];
  warnings: NodeWarning[];
  persistenceList: Record<string, string[]>;
  effectiveHeight: number; // Total height including warning bar if present
  outputXPositions: Map<number, number>; // outputIndex -> X position (in layout units, before pixel conversion)
  reachable: boolean;
}

interface SwapButton {
  stepId: string;
  x: number;
  y: number;
}

interface PathDefinition {
  id: string;
  path: string;
  hasWarning?: boolean;
  warningSeverity?: 'error' | 'warning' | 'info';
  parentId?: string;
  parentIds?: string[];
  childId?: string;
  parentOutput?: number;
  canRemove?: boolean;
}

interface BranchWarningPosition {
  id: string;
  x: number;
  y: number;
  severity: 'error' | 'warning' | 'info';
  messages: string[];
  parentId: string;
}

interface OutputLabelPosition {
  id: string;
  label: string;
  x: number;
  y: number;
  color: string;
}

interface AddButtonPosition {
  id: string;
  parentStepId: string;
  outputIndex: number;
  top: number;
  left: number;
}

const BOX_WIDTH = 320;
const BOX_HEIGHT = 60; // Content height (actual rendered height)
const WARNING_BAR_HEIGHT = 52; // Height of the warning bar when displayed (padding + text + margin)
// Hauteur supposée du panneau de statistiques, le temps que la hauteur réelle
// soit mesurée. Une carte va d'un chiffre seul à un entonnoir suivi de ses
// motifs d'arrêt : viser trop bas fait sauter la mise en page à l'ouverture.
const ANALYTICS_CHIPS_HEIGHT = 90;
const HORIZONTAL_GAP = 60;
const VERTICAL_GAP = 100; // Gap between levels (increased for better spacing)
const FREE_STUB_LENGTH = 50; // Length of stub line for free (unconnected) outputs
const LEVEL_HEIGHT = BOX_HEIGHT + VERTICAL_GAP; // Total height per level for layout
const CANVAS_MARGIN = 140;
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 2;
const ZOOM_STEP = 0.25;

export default defineComponent({
  name: 'FlowRenderer',
  components: {
    Button,
    FlowNode,
    FlowAddButton,
    FlowOperatorPanel,
    FlowEmptyState,
    FlowBranchMenu,
    FlowBranchReorderModal,
    FlowSwapButton,
  },
  props: {
    flow: {
      type: Object as PropType<FlowStructureDto | null>,
      default: null,
    },
    type: {
      type: String as PropType<FlowStructureType>,
      default: TypeCampaignEnum.AUTOMATION,
    },
    emptyLabel: {
      type: String,
      default: 'Aucun élément à afficher pour ce flow.',
    },
  },
  emits: ['add-operator', 'update-step', 'delete-step', 'replace-step', 'menu-action', 'start-flow', 'open-settings', 'decrease-branches', 'merge-branch', 'add-operator-closure', 'reorder-branches', 'remove-connection'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const { validateOperator } = useNodeWarnings();
    const canvasWrapper = ref<HTMLDivElement | null>(null);
    const zoom = ref(1);

    // Track real heights of nodes (updated via resize events)
    const realNodeHeights = ref<Map<string, number>>(new Map());

    const handleNodeResize = (event: { stepId: string; height: number }) => {
      // Only update if significative change to avoid loops
      const current = realNodeHeights.value.get(event.stepId);
      if (!current || Math.abs(current - event.height) > 2) {
        const newMap = new Map(realNodeHeights.value);
        newMap.set(event.stepId, event.height);
        realNodeHeights.value = newMap;
      }
    };

    const translate = reactive({ x: 0, y: 0 });
    const isPanning = ref(false);
    const panStart = reactive({ x: 0, y: 0, translateX: 0, translateY: 0 });
    const containerSize = reactive({ width: 0, height: 0 });

    // Analytics mode - used to recalculate layout when analytics chips are shown
    const flowAnalytics = useGlobalFlowAnalytics();

    /**
     * Mode contact : un step est « traversé » quand le composable analytics
     * a une entrée pour son id (le backend ne marque que les steps du parcours).
     * Utilisé pour colorer les edges et dimmer les nodes hors parcours.
     */
    const isStepTraversed = (id?: string | null): boolean => {
      if (!id || !flowAnalytics.isAnalyticsMode.value) return false;
      return !!flowAnalytics.analyticsData.value[id];
    };

    // Warnings state - stored separately and updated asynchronously
    const nodeWarnings = ref<Map<string, NodeWarning[]>>(new Map());

    // Loading state per node (e.g. during WhatsApp validation submission)
    const loadingStepIds = ref<Set<string>>(new Set());

    // Hovered warning parent ID for highlighting the corresponding branch
    const hoveredWarningParentId = ref<string | null>(null);
    const hoveredNodeId = ref<string | null>(null);

    // Hovered removable connection for highlighting the branch
    const hoveredRemovableConnection = ref<{ parentId: string; childId: string } | null>(null);

    // Target bulk calculations (only for bulk campaigns)
    const { targetCalculations, calculateTargetBulk, getStepCalculation } = useTargetBulkCalculations();

    const getTargetCalculation = (stepId: string) => {
      if (props.type !== TypeCampaignEnum.BULK || flowAnalytics.isAnalyticsMode.value) return null;
      return getStepCalculation(stepId);
    };

    // WhatsApp polling manager for PENDING templates
    const whatsappPollingManager = useWhatsappPollingManager();

    // Linked operator selection mode
    const {
      isActive: isSelectionModeActive,
      selectableTypes,
      selectableStepIds,
      selectedStepIds,
      toggleStepSelection,
      isStepSelected,
      confirmSelection,
      cancelSelection,
    } = useLinkedOperatorSelection();

    // Check if a step is selectable in selection mode (must be correct type AND an ancestor)
    const isStepSelectable = (stepType: string, stepId: string): boolean => {
      const isCorrectType = selectableTypes.value.includes(stepType);
      const isAncestor = selectableStepIds.value.length === 0 || selectableStepIds.value.includes(stepId);
      return isCorrectType && isAncestor;
    };

    // Linked operator highlight (for hovering over filters with dependencies)
    const { setHighlightedSteps, clearHighlightedSteps, isStepHighlighted } = useLinkedOperatorHighlight();

    // Extract linked_box from step parameters
    const getLinkedStepIds = (stepId: string): string[] => {
      const step = props.flow?.steps.find(s => s.id === stepId);
      if (!step) return [];

      const params = parseOperatorParameters(step.parameters);
      const linkedBox = params.linked_box;
      if (!linkedBox) return [];

      let linkedArray: string[] = [];
      if (Array.isArray(linkedBox)) {
        linkedArray = linkedBox;
      } else if (typeof linkedBox === 'string') {
        try {
          const parsed = JSON.parse(linkedBox);
          linkedArray = Array.isArray(parsed) ? parsed : [];
        } catch {
          linkedArray = [];
        }
      }

      return linkedArray;
    };

    const isRelatedToHoveredNode = (parentId: string): boolean => {
      if (!parentId || !hoveredNodeId.value) return false;
      if (parentId === hoveredNodeId.value) return true;
      // Handle Spacers/Phantoms: __spacer_SOURCE_TARGET_LAYER
      if (parentId.startsWith('__spacer') || parentId.startsWith('__phantom')) {
        const parts = parentId.split('_');
        // ["", "", "spacer", "sourceId", ...]
        return parts[3] === hoveredNodeId.value;
      }
      return false;
    };

    const onNodeMouseEnter = (stepId: string) => {
      hoveredNodeId.value = stepId;
      const linkedIds = getLinkedStepIds(stepId);
      if (linkedIds.length > 0) {
        setHighlightedSteps(linkedIds);
      }
    };

    const onNodeMouseLeave = () => {
      hoveredNodeId.value = null;
      clearHighlightedSteps();
    };

    // Reset selection mode when flow changes (e.g., switching scenarios)
    watch(() => props.flow, (newFlow, oldFlow) => {
      if (oldFlow && newFlow !== oldFlow && isSelectionModeActive.value) {
        forceResetSelection();
      }
    });

    // Handle update-step from operator panel (e.g. WhatsApp submit from form)
    const onPanelUpdateStep = (payload: { stepId: string; data: Record<string, any> }) => {
      emit('update-step', payload);
      // Re-validate warnings for this step
      const step = props.flow?.steps.find(s => s.id === payload.stepId);
      if (step) {
        validateStep(step, props.flow!, props.type);
      }
    };

    // Handle WhatsApp validation status change from polling
    const handleWhatsappStatusChange = (stepId: string, status: WhatsappValidationStatus) => {
      // Emit update to parent to update the step data
      emit('update-step', {
        stepId,
        data: {
          template_validation_status: status,
          // Disable operator if status is blocking
          ...(status !== WhatsappValidationStatus.APPROVED ? { enabled_operator: 0 } : {}),
        },
      });
      // Re-validate warnings for this step
      const step = props.flow?.steps.find(s => s.id === stepId);
      if (step) {
        validateStep(step, props.flow!, props.type);
      }
    };

    // Sync WhatsApp validation status from templateConfig for all WhatsApp steps
    // This ensures that if a template was submitted externally or the user left without saving,
    // the step's template_validation_status reflects the real status from the template
    const syncedWhatsappSteps = ref<Set<string>>(new Set());
    const whatsappSyncCompleted = ref(false);
    const syncWhatsappValidationStatuses = async () => {
      const flow = props.flow;
      const shopId = UserState.activeShop?.id;
      if (!flow || !shopId) {
        whatsappSyncCompleted.value = true;
        return;
      }

      for (const step of flow.steps) {
        if (step.type !== 'boxsendwhatsapp') continue;
        // Only sync once per step
        if (syncedWhatsappSteps.value.has(step.id)) continue;

        const params = parseOperatorParameters(step.parameters);
        const templateId = params?.template_display_selector_id;
        if (!templateId || Number(templateId) <= 0) continue;

        // Show loading on the node while syncing
        loadingStepIds.value = new Set([...loadingStepIds.value, step.id]);

        try {
          const templates = await getTemplateAndRedirect(shopId, Number(templateId));
          if (!templates || templates.length === 0) continue;

          const templateConfig = JSON.parse(templates[0].template_config ?? '{}');
          const currentStatus = params.template_validation_status;

          // Collect all statuses: root + funnels
          const allStatuses: (string | null)[] = [templateConfig.templateValidationStatus || null];

          // Check funnel templates if present
          if (templateConfig.funnels && Array.isArray(templateConfig.funnels)) {
            for (const funnel of templateConfig.funnels) {
              if (funnel.type === 'whatsapp' && funnel.id) {
                try {
                  const funnelTemplates = await getTemplateAndRedirect(shopId, Number(funnel.id));
                  if (funnelTemplates && funnelTemplates.length > 0) {
                    const funnelConfig = JSON.parse(funnelTemplates[0].template_config ?? '{}');
                    allStatuses.push(funnelConfig.templateValidationStatus || null);
                  } else {
                    allStatuses.push(null);
                  }
                } catch {
                  allStatuses.push(null);
                }
              }
            }
          }

          // Calculate combined status: absent (NOT_SUBMITTED) > blocking > pending > approved
          let combinedStatus: WhatsappValidationStatus;
          const hasNotSubmitted = allStatuses.some((s) => !s);
          const hasBlocking = allStatuses.some((s) => s && isBlockingWhatsappStatus(s) && !isPendingWhatsappStatus(s));
          const hasPending = allStatuses.some((s) => s && isPendingWhatsappStatus(s));

          if (hasNotSubmitted) {
            combinedStatus = WhatsappValidationStatus.NOT_SUBMITTED;
          } else if (hasBlocking) {
            const blockingStatus = allStatuses.find((s) => s && isBlockingWhatsappStatus(s) && !isPendingWhatsappStatus(s));
            combinedStatus = (blockingStatus as WhatsappValidationStatus) || WhatsappValidationStatus.REJECTED;
          } else if (hasPending) {
            combinedStatus = WhatsappValidationStatus.PENDING;
          } else {
            combinedStatus = WhatsappValidationStatus.APPROVED;
          }

          if (combinedStatus !== currentStatus) {
            handleWhatsappStatusChange(step.id, combinedStatus);
          }
        } catch (error) {
          console.warn(`[WhatsApp Sync] Failed to sync status for step ${step.id}:`, error);
        } finally {
          const updated = new Set(loadingStepIds.value);
          updated.delete(step.id);
          loadingStepIds.value = updated;
        }

        syncedWhatsappSteps.value.add(step.id);
      }

      whatsappSyncCompleted.value = true;
    };

    // Handle warning action button clicks (generic handler)
    const handleWarningAction = async (event: string, step: FlowStructureDto['steps'][number]) => {
      if (event === 'submit-whatsapp-validation') {
        const shopId = UserState.activeShop?.id;
        const params = parseOperatorParameters(step.parameters);
        if (!shopId || !params?.template_display_selector_id) return;

        // Show loading on the node
        loadingStepIds.value = new Set([...loadingStepIds.value, step.id]);

        try {
          const { SubmitWhatsappValidation } = await import('@/composables/services/Whatsapp');
          const response = await SubmitWhatsappValidation(shopId, params);

          if (response.success && response.data && response.data.template) {
            const { status } = response.data.template;
            const mappedStatus = (WhatsappValidationStatus as any)[status] || WhatsappValidationStatus.PENDING;
            handleWhatsappStatusChange(step.id, mappedStatus);
          } else {
            handleWhatsappStatusChange(step.id, WhatsappValidationStatus.ERROR);
          }
        } catch (error) {
          console.error('[WhatsApp Submit] Failed to submit for validation:', error);
          handleWhatsappStatusChange(step.id, WhatsappValidationStatus.ERROR);
        } finally {
          const updated = new Set(loadingStepIds.value);
          updated.delete(step.id);
          loadingStepIds.value = updated;
        }
      }
    };

    const nodes = computed<LayoutNode[]>(() => {
      const flow = props.flow;
      if (!flow || !flow.steps.length) {
        return [];
      }

      // --- 1. Data Structure & Graph Construction ---
      const byId = new Map<string, FlowStructureDto['steps'][number]>();
      flow.steps.forEach((step) => byId.set(step.id, step));

      // Helper to get output count
      const getOutputCount = (stepId: string): number => {
        const step = byId.get(stepId);
        const operator = step ? getOperatorById(step.type) : null;
        const rawCount = operator?.outputs ? operator.outputs(step?.parameters || {}) : 1;
        return typeof rawCount === 'string' ? parseInt(rawCount, 10) : rawCount;
      };

      // Adjacency List: Parent -> Children (with output index)
      // We rely strictly on flow.connections to ignore "zombies"
      const childrenMap = new Map<string, { id: string; parentOutput: number }[]>();
      const parentsMap = new Map<string, { id: string; parentOutput: number }[]>();

      // Initialize maps for all steps to avoid undefined checks
      flow.steps.forEach(step => {
        childrenMap.set(step.id, []);
        parentsMap.set(step.id, []);
      });

      flow.connections.forEach(conn => {
        // Validate existence
        if (!byId.has(conn.parentId) || !byId.has(conn.childId)) return;

        // Add to mapped arrays
        childrenMap.get(conn.parentId)?.push({ id: conn.childId, parentOutput: conn.parentOutput });
        parentsMap.get(conn.childId)?.push({ id: conn.parentId, parentOutput: conn.parentOutput });
      });

      // --- 2. Phase 1: Layer Assignment (Rank) ---
      // Rank = Max(ParentRank) + 1
      const memoDepth = new Map<string, number>();
      const computeLayer = (id: string, pathStack: Set<string> = new Set()): number => {
        if (memoDepth.has(id)) return memoDepth.get(id)!;
        if (pathStack.has(id)) return 0; // Cycle detection

        const parents = parentsMap.get(id) || [];
        if (parents.length === 0) {
          memoDepth.set(id, 0);
          return 0;
        }

        pathStack.add(id);
        let maxParentLayer = -1;
        parents.forEach(p => {
          const pLayer = computeLayer(p.id, pathStack);
          if (pLayer > maxParentLayer) maxParentLayer = pLayer;
        });
        pathStack.delete(id);

        const myLayer = maxParentLayer + 1;
        memoDepth.set(id, myLayer);
        return myLayer;
      };

      // Compute layers for ALL steps
      let maxLayer = 0;
      const nodesByLayer = new Map<number, string[]>();

      const phantomNodes = new Set<string>(); // Track IDs of phantom nodes
      const spacerSources = new Map<string, string>(); // Track Original Source ID for Spacers

      flow.steps.forEach(step => {
        const layer = computeLayer(step.id);
        if (!nodesByLayer.has(layer)) nodesByLayer.set(layer, []);
        nodesByLayer.get(layer)!.push(step.id);
        if (layer > maxLayer) maxLayer = layer;
      });

      // --- 2.5: Phantom Node Injection (For Symmetry & Collision) ---
      // Apply to ALL nodes. If an output is unconnected, inject a "Phantom" node
      // so that layout layout engine reserves space (preventing collision with neighbors).
      flow.steps.forEach(step => {
        const outputCount = getOutputCount(step.id);
        if (outputCount === 0) return; // Nothing to draw

        const layer = memoDepth.get(step.id) ?? 0;
        const childLayer = layer + 1;

        for (let i = 1; i <= outputCount; i++) {
          // Check if connected
          const isConnected = childrenMap.get(step.id)?.some(c => c.parentOutput === i);

          if (!isConnected) {
            // Create Phantom
            const phantomId = `__phantom_${step.id}_${i}`;
            phantomNodes.add(phantomId);

            // Add to maps
            if (!childrenMap.has(step.id)) childrenMap.set(step.id, []);
            childrenMap.get(step.id)!.push({ id: phantomId, parentOutput: i });

            parentsMap.set(phantomId, [{ id: step.id, parentOutput: i }]);
            childrenMap.set(phantomId, []);

            // Add to Layer
            if (!nodesByLayer.has(childLayer)) nodesByLayer.set(childLayer, []);
            nodesByLayer.get(childLayer)!.push(phantomId);

            if (childLayer > maxLayer) maxLayer = childLayer;
            memoDepth.set(phantomId, childLayer);
          }
        }
      });

      // --- 2.6: Spacer Node Injection (Long Edge Handling) ---
      // For connections spanning > 1 layer, inject "Spacer" nodes in intermediate layers
      // to reserve vertical columns for the edge to pass through.
      // E.g., L0 -> L2 becomes L0 -> Spacer(L1) -> L2
      // Use efficient iteration over original connections
      const handledConnections = new Set<string>();

      flow.steps.forEach(step => {
        const pLayer = memoDepth.get(step.id);
        if (pLayer === undefined) return;

        // Iterate original children to find long edges
        // Note: We use childrenMap which might already contain Phantoms, but Phantoms are L+1, so rank diff is 1.
        // We care about REAL children spanning layers.
        const children = childrenMap.get(step.id) || [];

        // Using a new list to safely modify graph structure without iterating modified list
        const originalChildren = [...children];

        originalChildren.forEach(childRef => {
          const cLayer = memoDepth.get(childRef.id);
          if (cLayer === undefined) return;

          // If connection spans more than 1 layer (e.g. 0 -> 2, diff=2)
          if (cLayer > pLayer + 1) {
            // Break the edge P -> C into P -> S1 -> S2 -> C

            // 1. Remove direct connection P->C from layout maps (to avoid duplicate pull)
            // Filter out the childRef from parent's children
            const currentChildren = childrenMap.get(step.id) || [];
            childrenMap.set(step.id, currentChildren.filter(c => c.id !== childRef.id));

            // Filter out the parentRef from child's parents
            const currentParents = parentsMap.get(childRef.id) || [];
            parentsMap.set(childRef.id, currentParents.filter(p => p.id !== step.id));

            // 2. Create Chain
            let prevId = step.id;
            let prevLayer = pLayer;

            for (let l = pLayer + 1; l < cLayer; l++) {
              const spacerId = `__spacer_${step.id}_${childRef.id}_${l}`;
              phantomNodes.add(spacerId);

              // Start/Internal of chain
              // Parent -> Spacer
              // For the first spacer, parentOutput is preserved from original connection.
              // For subsequent spacers, it's irrelevant (output 1).
              const parentOut = (l === pLayer + 1) ? childRef.parentOutput : 1;

              // Add Spacer to graph
              if (!childrenMap.has(prevId)) childrenMap.set(prevId, []);
              childrenMap.get(prevId)!.push({ id: spacerId, parentOutput: parentOut });

              parentsMap.set(spacerId, [{ id: prevId, parentOutput: parentOut }]);
              childrenMap.set(spacerId, []);

              // Add to nodesByLayer
              if (!nodesByLayer.has(l)) nodesByLayer.set(l, []);
              nodesByLayer.get(l)!.push(spacerId);
              memoDepth.set(spacerId, l);

              // Track Source to correctly identify the original parent count for weighted alignment
              spacerSources.set(spacerId, step.id);

              // Advance
              prevId = spacerId;
              prevLayer = l;
            }

            // 3. Connect Last Spacer -> Original Child
            if (!childrenMap.has(prevId)) childrenMap.set(prevId, []);
            childrenMap.get(prevId)!.push({ id: childRef.id, parentOutput: 1 });

            // Add spacer as parent to child
            const childParents = parentsMap.get(childRef.id) || [];
            childParents.push({ id: prevId, parentOutput: 1 });
            parentsMap.set(childRef.id, childParents);
          }
        });
      });

      // --- 3. Phase 2: Ordering (Minimizing Crossings) ---
      // Barycenter Heuristic
      // Start from layer 1 (layer 0 is usually fixed or just the root)
      for (let l = 1; l <= maxLayer; l++) {
        const currentNodes = nodesByLayer.get(l) || [];
        const prevNodes = nodesByLayer.get(l - 1) || [];

        // Map previous layer nodes to their index for quick lookup
        const prevNodeIndex = new Map<string, number>();
        prevNodes.forEach((id, idx) => prevNodeIndex.set(id, idx));

        currentNodes.sort((aId, bId) => {
          const getBarycenter = (nodeId: string) => {
            const parents = parentsMap.get(nodeId) || [];
            if (parents.length === 0) return 0;
            let sum = 0;
            let count = 0;
            parents.forEach(p => {
              const pIndex = prevNodeIndex.get(p.id);
              if (pIndex !== undefined) {
                // Weight by output index: connections from higher output indices pull to the right
                // Add fractional value based on output index (e.g., +0.1 for output 1, +0.2 for output 2)
                sum += pIndex + (p.parentOutput * 0.1);
                count++;
              }
            });
            return count === 0 ? 0 : sum / count;
          };

          const bcA = getBarycenter(aId);
          const bcB = getBarycenter(bId);

          if (Math.abs(bcA - bcB) > 0.0001) {
            return bcA - bcB;
          }

          // Secondary Sort: Maintain order of outputs from the SAME parent
          // If they share a parent, the one with the lower parentOutput index comes first
          const parentsA = parentsMap.get(aId) || [];
          const parentsB = parentsMap.get(bId) || [];

          if (parentsA.length > 0 && parentsB.length > 0) {
            // Simple heuristic: compare primary parents
            // If they have the same primary parent, sort by output index
            const pA = parentsA[0];
            const pB = parentsB[0];

            if (pA.id === pB.id) {
              return pA.parentOutput - pB.parentOutput;
            }
            // If parents are different but mapped to same barycenter (rare collision or same-index parents),
            // fall back to purely deterministic ID sort to stop jumping
            return pA.id.localeCompare(pB.id);
          }

          return aId.localeCompare(bId);
        });
      }

      // --- 4. Phase 3: Coordinate Assignment (X-Pos) & Collision Resolution ---
      // "Solid Wall" Algorithm
      const xPositions = new Map<string, number>(); // Stores center X

      // Initialize Root at 0
      if (flow.rootStepId) xPositions.set(flow.rootStepId, 0);

      const spacing = BOX_WIDTH + HORIZONTAL_GAP;

      // Helper to calculate the effective width required by a node's children fan-out
      // This is crucial for A/B testing nodes or any node with > 2 outputs
      const getRequiredHalfWidth = (id: string): number => {
        // Extra buffer for Phantoms/Spacers to ensure they don't visually "touch" real nodes
        if (id.startsWith('__phantom') || id.startsWith('__spacer')) {
          return (BOX_WIDTH / 2);
        }

        const count = getOutputCount(id);
        if (count <= 1) return BOX_WIDTH / 2;
        // Distance from center to the center of the outermost output
        const halfFanOut = ((count - 1) * spacing) / 2;
        // Add half box width to cover the actual visual node at that position
        // This ensures that the edge of the furthest child/phantom doesn't collide with the neighbor
        return halfFanOut + BOX_WIDTH / 2;
      };

      // Forward Pass (Place & Push)
      for (let l = 0; l <= maxLayer; l++) {
        const nodesInLayer = nodesByLayer.get(l) || [];

        // Step A: Naive Placement from Parents (Weighted for Left-Stability)
        if (l > 0) {
          nodesInLayer.forEach(id => {
            const parents = parentsMap.get(id) || [];
            if (parents.length > 0) {
              // Collect parent positions
              // Heuristic:
              // 1. Identify "Structural" Parents (Multi-Output or Spacers) vs "Sequential" Parents (Single-Output).
              // 2. Structural Parents define a major branch lane and act as Anchors.
              // 3. Sequential Parents are flexible steps that should align diagonally to the Anchor.

              const parentPoints: { x: number, weight: number }[] = [];
              let totalWeight = 0;
              let weightedSum = 0;

              parents.forEach(p => {
                const pX = xPositions.get(p.id) ?? 0;

                // Determine Effective Output Count (Structural vs Sequential)
                let effectiveOutputCount = 1;
                let sourceId = p.id;
                let isSpacer = p.id.startsWith('__spacer');

                if (isSpacer) {
                  // Robust Lookup: Use the explicit source map
                  const cursorId = spacerSources.get(p.id);
                  if (cursorId) {
                    sourceId = cursorId;
                    effectiveOutputCount = getOutputCount(cursorId);
                  } else {
                    console.warn(`[FlowRenderer] Spacer ${p.id} has no source map entry.`);
                  }
                } else {
                  effectiveOutputCount = getOutputCount(p.id);
                }

                const offsetCalcCount = p.id.startsWith('__spacer') ? 1 : effectiveOutputCount;

                const offset = (p.parentOutput - 1) - (offsetCalcCount - 1) / 2;
                const exitX = pX + offset * spacing;

                // Priority Rule:
                // - Splitters (Multi-Output) are the Main Structural Trunks (Highest Weight).
                // - Spacers from Single-Output nodes are Long Distance Edges (Medium Weight).
                // - Short Sequential nodes are Flexible (Low Weight).
                let weight = 1;
                if (effectiveOutputCount > 1) {
                    weight = 5000;
                } else if (p.id.startsWith('__spacer')) {
                    weight = 1000;
                } else {
                    weight = 1;
                }

                weightedSum += exitX * weight;
                totalWeight += weight;
              });

              if (totalWeight > 0) {
                const finalX = weightedSum / totalWeight;
                xPositions.set(id, finalX);
              } else {
                xPositions.set(id, xPositions.get(id) ?? 0);
              }
            } else {
              // Disconnected / new root in this layer? Keep 0
              xPositions.set(id, 0);
            }
          });
        }

        // Step B: Resolve Collisions (Left-to-Right)
        // 1. Sort by current desired X
        nodesInLayer.sort((a, b) => (xPositions.get(a) ?? 0) - (xPositions.get(b) ?? 0));

        // 2. Enforce minimum distance with Fan-Out Awareness
        for (let i = 1; i < nodesInLayer.length; i++) {
          const leftNode = nodesInLayer[i - 1];
          const rightNode = nodesInLayer[i];

          const leftX = xPositions.get(leftNode) ?? 0;
          const rightX = xPositions.get(rightNode) ?? 0;

          // Dynamic spacing based on subtree width
          const leftHalf = getRequiredHalfWidth(leftNode);
          const rightHalf = getRequiredHalfWidth(rightNode);
          const requiredSpacing = leftHalf + rightHalf + HORIZONTAL_GAP;

          if (rightX < leftX + requiredSpacing) {
            const newX = leftX + requiredSpacing;
            xPositions.set(rightNode, newX);
          }
        }
      }

      // Backward Pass (Centering Parents over Children)
      // Iterate Bottom -> Up
      for (let l = maxLayer - 1; l >= 0; l--) {
        const nodesInLayer = nodesByLayer.get(l) || [];
        // Re-sort based on current positions to ensure Left-to-Right processing
        nodesInLayer.sort((a, b) => (xPositions.get(a) ?? 0) - (xPositions.get(b) ?? 0));

        const newPositions = new Map<string, number>();

        // 1. Calculate ideal positions
        nodesInLayer.forEach(id => {
          const children = childrenMap.get(id) || [];
          if (children.length > 0) {
            // Calculate center of children span
            let minC = Infinity, maxC = -Infinity;
            children.forEach(c => {
              const cx = xPositions.get(c.id);
              if (cx !== undefined) {
                if (cx < minC) minC = cx;
                if (cx > maxC) maxC = cx;
              }
            });

            if (minC !== Infinity) {
              let centerC = (minC + maxC) / 2;

              // Yield Logic:
              // If a Sequential Parent shares a child (merge node) with ANY other parent,
              // freeze position to prevent collision cascade.
              // Without this, two parents both centering over the same merge child
              // causes collision resolution to push one parent further away,
              // creating long diagonal lines.
              if (getOutputCount(id) === 1 && children.length === 1) {
                const childId = children[0].id;
                const childParents = parentsMap.get(childId) || [];
                const hasOtherParent = childParents.some(p => p.id !== id);

                if (hasOtherParent) {
                  centerC = xPositions.get(id) ?? centerC;
                }
              }

              newPositions.set(id, centerC);
            } else {
              newPositions.set(id, xPositions.get(id)!);
            }
          } else {
            newPositions.set(id, xPositions.get(id)!);
          }
        });

        // 2. Apply new positions WITH Collision Check (Left-to-Right) similar to Forward Pass
        // 2. Apply new positions WITH Collision Check (Left-to-Right) similar to Forward Pass
        // Sort nodes by original X first to maintain relative order
        nodesInLayer.sort((a, b) => (xPositions.get(a) ?? 0) - (xPositions.get(b) ?? 0));

        // Assign positions respecting "Solid Wall" from Left
        for (let i = 0; i < nodesInLayer.length; i++) {
          const id = nodesInLayer[i];
          let desiredX = newPositions.get(id)!;

          // Check Left Sibling
          if (i > 0) {
            const leftId = nodesInLayer[i - 1];
            // Note: When moving parents, we should be careful not to create overlaps.
            // We must respect the committed position of the left neighbor WITH FAN-OUT.
            const leftX = xPositions.get(leftId)!;

            // Dynamic spacing based on subtree width (SAME AS FORWARD PASS)
            const leftHalf = getRequiredHalfWidth(leftId);
            const rightHalf = getRequiredHalfWidth(id);
            const requiredSpacing = leftHalf + rightHalf + HORIZONTAL_GAP;

            const minLeft = leftX + requiredSpacing;
            if (desiredX < minLeft) desiredX = minLeft;
          }

          // Update
          xPositions.set(id, desiredX);
        }
      }

      // --- Final Layout Generation ---

      let minX = Infinity, maxX = -Infinity;
      let minY = Infinity, maxY = -Infinity;

      const results: LayoutNode[] = [];
      const nodeOutputPositionsMap = new Map<string, Map<number, number>>();

      // --- 3.1 Calculate Layer Heights (Dynamic Y) ---
      // We must determine the height of each layer based on the tallest node in it.
      const layerMaxHeights = new Map<number, number>();

      // Initialize with default height
      for (let l = 0; l <= maxLayer; l++) {
        layerMaxHeights.set(l, BOX_HEIGHT);
      }

      // First pass: find max height per layer
      flow.steps.forEach(step => {
        const layer = memoDepth.get(step.id) ?? 0;

        // Calculate Effective Height
        const analyticsForStep = flowAnalytics.analyticsData.value[step.id];
        const hasAnalyticsChips = flowAnalytics.isAnalyticsMode.value && !!analyticsForStep;

        const stepWarnings = nodeWarnings.value.get(step.id) || [];
        const hasBoxWarnings = stepWarnings.some(w => w.location === 'box');

        // Predicted
        let h = BOX_HEIGHT
          + (hasBoxWarnings ? WARNING_BAR_HEIGHT : 0)
          + (hasAnalyticsChips ? ANALYTICS_CHIPS_HEIGHT : 0);

        // Real/Measured (preferred)
        if (realNodeHeights.value.has(step.id)) {
          h = realNodeHeights.value.get(step.id)!;
        }

        if (h > (layerMaxHeights.get(layer) || 0)) {
          layerMaxHeights.set(layer, h);
        }
      });

      // Accumulate tops
      const layerTops = new Map<number, number>();
      let currentTop = 0;
      for (let l = 0; l <= maxLayer; l++) {
        layerTops.set(l, currentTop);
        // Next layer starts after this layer's max height + gap
        currentTop += (layerMaxHeights.get(l) || BOX_HEIGHT) + VERTICAL_GAP;
      }

      // Generate LayoutNodes
      flow.steps.forEach(step => {
        const layer = memoDepth.get(step.id) ?? 0;
        const x = xPositions.get(step.id) ?? 0;
        // Keep vertical alignment logic but use dynamic layer tops
        const y = layerTops.get(layer) ?? 0;

        const operator = getOperatorById(step.type);
        const dynamicLabel = operator?.getDisplayLabel?.(step.parameters || {}, t);
        const label = dynamicLabel ?? (operator ? t(operator.label) : step.type);
        const kind = operator?.kind ?? step.class ?? 'action';
        const dynamicSubtitle = operator?.getSubtitle?.(step.parameters || {}, t);
        const subtitle = dynamicSubtitle ?? '';
        const icon = operator?.icon ?? 'fa-box';
        const nodeKind = kind;

        const centerX = x;
        const top = y;
        const left = centerX - BOX_WIDTH / 2;
        const isRoot = step.id === flow.rootStepId;
        const outputCount = getOutputCount(step.id);
        const connectedOutputs = (step.children || []).map((c) => c.output);

        const persistenceList = calculatePersistenceForStep(flow, step.id);
        const stepWarnings = nodeWarnings.value.get(step.id) || [];
        const hasBoxWarnings = stepWarnings.some(w => w.location === 'box');

        // Check if analytics chips should be shown for this step
        // Access analyticsData.value directly to ensure reactivity
        const analyticsForStep = flowAnalytics.analyticsData.value[step.id];
        const hasAnalyticsChips = flowAnalytics.isAnalyticsMode.value && !!analyticsForStep;

        // Calculate predicted height (fallback)
        let effectiveHeight = BOX_HEIGHT
          + (hasBoxWarnings ? WARNING_BAR_HEIGHT : 0)
          + (hasAnalyticsChips ? ANALYTICS_CHIPS_HEIGHT : 0);

        // Use real measured height if available (handling expansion etc.)
        if (realNodeHeights.value.has(step.id)) {
          effectiveHeight = realNodeHeights.value.get(step.id)!;
        }

        // Calculate Output Start Positions (anchors for drawing lines)
        const outXPositions = new Map<number, number>();

        const spacing = BOX_WIDTH + HORIZONTAL_GAP; // Ensure this scope is available or re-calculated

        for (let i = 1; i <= outputCount; i++) {
          // 1. Check if there is a Phantom Node for this output
          // Phantom ID format: `__phantom_${step.id}_${i}`
          const phantomId = `__phantom_${step.id}_${i}`;
          const phantomX = xPositions.get(phantomId);

          if (phantomX !== undefined) {
            // Use the layout-calculated position of the phantom
            outXPositions.set(i, phantomX);
          } else {
          // 2. Connected Output or Single Output (no phantom injected)
            // Naive fallback relative to parent center (classic tree)
            // Note: If connected, the link will eventually target the child center anyway.
            // This position is mainly used for the "Shoulder" start point if needed,
            // or for cases where phantom wasn't injected (e.g. outputCount=1).
            const offset = (i - 1) - (outputCount - 1) / 2;
            outXPositions.set(i, centerX + offset * spacing);
          }
        }

        // Bounds Calculation
        let nodeMinX = left;
        let nodeMaxX = left + BOX_WIDTH;
        // Include output anchors in bounds (if wide branching)
        if (outputCount > 1) {
          outXPositions.forEach(ox => {
            if (ox < nodeMinX) nodeMinX = ox;
            if (ox > nodeMaxX) nodeMaxX = ox;
          });
        }

        if (nodeMinX < minX) minX = nodeMinX;
        if (nodeMaxX > maxX) maxX = nodeMaxX;
        if (top < minY) minY = top;
        if (top + effectiveHeight > maxY) maxY = top + effectiveHeight;

        results.push({
          step,
          label,
          subtitle,
          icon,
          kind: nodeKind,
          left,
          top,
          centerX,
          hasInput: !isRoot,
          outputs: outputCount,
          connectedOutputs,
          warnings: stepWarnings,
          reachable: true, // Sugiyama traversal implicitly handles this? We need to keep the flag if interface demands it
          persistenceList,
          effectiveHeight,
          outputXPositions: outXPositions,
        });
      });

      if (!results.length) return [];

      // Global Shift to Margin
      const shiftX = (Number.isFinite(minX) ? minX : 0) - CANVAS_MARGIN;
      const shiftY = (Number.isFinite(minY) ? minY : 0) - CANVAS_MARGIN;

      results.forEach(node => {
        node.left -= shiftX;
        node.top -= shiftY;
        node.centerX -= shiftX;
        const newOuts = new Map<number, number>();
        node.outputXPositions.forEach((val, key) => {
          newOuts.set(key, val - shiftX);
        });
        node.outputXPositions = newOuts;
      });

      canvasSize.width = Math.max((Number.isFinite(maxX) ? maxX - shiftX : BOX_WIDTH) + CANVAS_MARGIN, BOX_WIDTH);
      canvasSize.height = Math.max((Number.isFinite(maxY) ? maxY - shiftY : BOX_HEIGHT) + CANVAS_MARGIN, BOX_HEIGHT);

      return results;
    });


    const canvasSize = reactive({ width: BOX_WIDTH + CANVAS_MARGIN, height: BOX_HEIGHT + CANVAS_MARGIN });

    // Track validated step signatures to avoid re-validating unchanged steps
    const validatedStepSignatures = ref<Map<string, string>>(new Map());

    // Generate a signature for a step to detect changes
    // Include parent types so that when a parent is replaced, children are revalidated
    const getStepSignature = (step: FlowStepDto): string => {
      const parentTypes = step.parents.map((p) => {
        const parentStep = props.flow?.steps.find((s) => s.id === p.id);
        return parentStep?.type || 'unknown';
      });
      return JSON.stringify({
        type: step.type,
        parameters: step.parameters,
        parents: step.parents,
        primaryParent: step.primaryParent,
        parentTypes,
      });
    };

    // Validate a single step
    const validateStep = (step: FlowStepDto, flow: FlowStructureDto, scenarioType: CampaignType) => {
      const parsedParams = parseOperatorParameters(step.parameters);

      // Get ALL parents for multi-parent nodes (closure nodes)
      const allParents = step.parents && step.parents.length > 0
        ? step.parents
        : (step.primaryParent ? [step.primaryParent] : []);

      const parentOperators: Array<{ id: string; type: string; data: any }> = [];
      allParents.forEach((parentRef) => {
        const parentStep = flow.steps.find((s) => s.id === parentRef.id);
        if (parentStep) {
          const parentParsedParams = parseOperatorParameters(parentStep.parameters);
          parentOperators.push({
            id: parentStep.id,
            type: parentStep.type,
            data: { id: parentStep.type, ...parentParsedParams },
          });
        }
      });

      const operatorData = { id: step.type, ...parsedParams };

      validateOperator(step.id, operatorData, {
        scenarioType,
        parentIds: parentOperators.map((p) => p.id),
        parentOperators,
        shopId: UserState.activeShop?.id,
        targetCalculation: getStepCalculation(step.id),
        flow,
      }).then((warnings) => {
        const updated = new Map(nodeWarnings.value);
        if (warnings.length > 0) {
          updated.set(step.id, warnings);
        } else {
          updated.delete(step.id);
        }
        nodeWarnings.value = updated;
      });
    };

    // Validate warnings for all nodes (progressive update, only changed steps)
    const validateAllWarnings = () => {
      const flow = props.flow;
      if (!flow || !flow.steps.length || flowAnalytics.isAnalyticsMode.value) {
        nodeWarnings.value = new Map();
        validatedStepSignatures.value = new Map();
        return;
      }

      const currentStepIds = new Set(flow.steps.map(s => s.id));
      const newSignatures = new Map<string, string>();

      // Remove warnings for deleted steps
      const updated = new Map(nodeWarnings.value);
      for (const stepId of updated.keys()) {
        if (!currentStepIds.has(stepId)) {
          updated.delete(stepId);
        }
      }
      nodeWarnings.value = updated;

      // Validate only new or changed steps
      flow.steps.forEach((step) => {
        const signature = getStepSignature(step);
        newSignatures.set(step.id, signature);

        const previousSignature = validatedStepSignatures.value.get(step.id);
        if (previousSignature !== signature) {
          // Step is new or changed, validate it
          validateStep(step, flow, props.type);
        }
      });

      validatedStepSignatures.value = newSignatures;
    };

    // Watch for flow changes and validate warnings
    watch(() => props.flow, () => {
      validateAllWarnings();
      // Sync WhatsApp validation statuses from templateConfig on load
      syncWhatsappValidationStatuses();
    }, { immediate: true, deep: true });

    // Watch for flow changes and calculate target bulk (only for bulk campaigns)
    watch(() => [props.flow, props.type], () => {
      if (props.type === TypeCampaignEnum.BULK && props.flow && props.flow.campaignId && !flowAnalytics.isAnalyticsMode.value) {
        const isCommercial = props.flow.settings?.commercial_campaign === 1;
        calculateTargetBulk(props.flow, props.flow.campaignId, isCommercial);
      }
    }, { immediate: true, deep: true });

    // Re-validate warnings when target calculations change (for 0 contacts warning)
    watch(targetCalculations, () => {
      validateAllWarnings();
    });

    // Watch for flow changes and update WhatsApp polling (only after sync is done)
    watch([() => props.flow?.steps, whatsappSyncCompleted], ([newSteps, syncDone]) => {
      if (!syncDone) return;
      if (!newSteps) {
        whatsappPollingManager.stopAllPolling();
        return;
      }
      // Prepare steps with parsed parameters for polling manager
      const stepsWithParams = (newSteps as FlowStructureDto['steps']).map(step => ({
        id: step.id,
        type: step.type,
        parameters: parseOperatorParameters(step.parameters),
      }));
      whatsappPollingManager.updatePolling(stepsWithParams, handleWhatsappStatusChange);
    }, { immediate: true, deep: true });

    // Cleanup polling on unmount
    onBeforeUnmount(() => {
      whatsappPollingManager.stopAllPolling();
    });

    const canvasTransform = computed(() => ({
      transform: `translate(${translate.x}px, ${translate.y}px) scale(${zoom.value})`,
      width: `${canvasSize.width}px`,
      height: `${canvasSize.height}px`,
    }));

    // Calculate horizontal position for an output based on stored positions from layout algorithm
    const getOutputX = (node: LayoutNode, outputIndex: number): number => {
      if (node.outputs === 1) {
        return node.centerX;
      }
      // Use pre-calculated output positions from layout algorithm
      const storedX = node.outputXPositions.get(outputIndex);
      if (storedX !== undefined) {
        return storedX;
      }
      // Fallback: calculate position based on output index (should not happen for processed nodes)
      const outputOffset = (outputIndex - 1) - (node.outputs - 1) / 2;
      const spacing = BOX_WIDTH + HORIZONTAL_GAP;
      return node.centerX + outputOffset * spacing;
    };

    // Unified Links Computation (Orthogonal Routing)
    const links = computed<PathDefinition[]>(() => {
      if (!props.flow) return [];
      const results: PathDefinition[] = [];
      const nodesById = new Map(nodes.value.map(n => [n.step.id, n]));

      nodes.value.forEach(node => {
        if (node.outputs === 0) return;

        // 1. Single Output Case (Simple Vertical Line)
        if (node.outputs === 1) {
          const startX = node.centerX;
          const startY = node.top + node.effectiveHeight;

          // Check connection
          const connection = props.flow!.connections.find(c => c.parentId === node.step.id);
          if (connection) {
            const child = nodesById.get(connection.childId);
            if (child) {
              // Draw straight vertical if aligned, or dogleg if not
                // Calculate warning severity for this branch
                let severity: 'error' | 'warning' | 'info' | undefined;
                const relevantWarnings = child.warnings.filter(w => w.location === 'branch' && (!w.parentId || w.parentId === node.step.id));
                if (relevantWarnings.some(w => w.severity === 'error')) severity = 'error';
                else if (relevantWarnings.some(w => w.severity === 'warning')) severity = 'warning';
                else if (relevantWarnings.some(w => w.severity === 'info')) severity = 'info';

                // Check if child has multiple parents (can remove this connection)
                // Count connections to this child from flow.connections
                const parentCount = props.flow!.connections.filter(c => c.childId === child.step.id).length;
                const canRemove = parentCount > 1;

                if (Math.abs(child.centerX - startX) < 2) {
                  const path = `M ${startX} ${startY} L ${child.centerX} ${child.top}`;
                  results.push({ id: `link-${node.step.id}-${child.step.id}`, path, warningSeverity: severity, parentId: node.step.id, childId: child.step.id, parentOutput: connection.parentOutput, canRemove });
                } else {
                  const midY = (startY + child.top) / 2;
                  const path = `M ${startX} ${startY} V ${midY} H ${child.centerX} V ${child.top}`;
                  results.push({ id: `link-${node.step.id}-${child.step.id}`, path, warningSeverity: severity, parentId: node.step.id, childId: child.step.id, parentOutput: connection.parentOutput, canRemove });
                }
            }
          } else {
            // Free output: No stub line - add button is flush against the box,
            // the dashed end-marker link will connect it to the flag below
          }
          return;
        }

        // 2. Multi-Output Case (Tree Trunk Style)
        const startX = node.centerX;
        const startY = node.top + node.effectiveHeight;
        const trunkY = startY + VERTICAL_GAP / 2;

        // Collect desired X positions for all outputs
        const outputTargets: { index: number, x: number, isConnected: boolean, childId?: string }[] = [];

        for (let i = 1; i <= node.outputs; i++) {
          const connection = props.flow!.connections.find(c => c.parentId === node.step.id && c.parentOutput === i);
          if (connection) {
            const child = nodesById.get(connection.childId);
            if (child) {
              outputTargets.push({ index: i, x: child.centerX, isConnected: true, childId: child.step.id });
            } else {
              // Fallback to naive
              const naiveX = getOutputX(node, i);
              outputTargets.push({ index: i, x: naiveX, isConnected: false });
            }
          } else {
            // Free output - Naive placement relative to parent OR relative to siblings?
            // Naive is safer for now.
            const naiveX = getOutputX(node, i);
            outputTargets.push({ index: i, x: naiveX, isConnected: false });
          }
        }

        if (outputTargets.length > 0) {
          const minTargetX = Math.min(...outputTargets.map(t => t.x));
          const maxTargetX = Math.max(...outputTargets.map(t => t.x));

          // Draw Trunk
          results.push({ id: `trunk-${node.step.id}`, path: `M ${startX} ${startY} V ${trunkY}`, parentId: node.step.id });

          // Draw Horizontal Bar (Shoulder) (Straight H)
          const barMin = Math.min(minTargetX, startX);
          const barMax = Math.max(maxTargetX, startX);
          results.push({ id: `shoulder-${node.step.id}`, path: `M ${barMin} ${trunkY} H ${barMax}`, parentId: node.step.id });

          // Draw Vertical Drops
          outputTargets.forEach(target => {
            if (target.isConnected) {
              const child = nodesById.get(target.childId!);
              if (child) {
                // Calculate warning severity for this branch
                let severity: 'error' | 'warning' | 'info' | undefined;
                const relevantWarnings = child.warnings.filter(w => w.location === 'branch' && (!w.parentId || w.parentId === node.step.id));
                if (relevantWarnings.some(w => w.severity === 'error')) severity = 'error';
                else if (relevantWarnings.some(w => w.severity === 'warning')) severity = 'warning';
                else if (relevantWarnings.some(w => w.severity === 'info')) severity = 'info';

                // Check if child has multiple parents (can remove this connection)
                // Count connections to this child from flow.connections
                const parentCount = props.flow!.connections.filter(c => c.childId === child.step.id).length;
                const canRemove = parentCount > 1;

                results.push({ id: `drop-${node.step.id}-${target.index}`, path: `M ${target.x} ${trunkY} L ${target.x} ${child.top}`, warningSeverity: severity, parentId: node.step.id, childId: child.step.id, parentOutput: target.index, canRemove });
              }
            } else {
              // Free stub
              const endY = trunkY + FREE_STUB_LENGTH;
              results.push({ id: `drop-free-${node.step.id}-${target.index}`, path: `M ${target.x} ${trunkY} L ${target.x} ${endY}`, parentId: node.step.id });
            }
          });
        }
      });

      // Sort results to ensure Error/Warning branches are drawn ON TOP of normal branches.
      // This solves the issue where a shared vertical drop (in a merge) is grey because
      // the grey branch was drawn last, obscuring the red branch.
      // Order: Normal < Info < Warning < Error.
      results.sort((a, b) => {
          const score = (s: string | undefined) => {
              if (s === 'info') return 1;
              if (s === 'warning') return 2;
              if (s === 'error') return 3;
              return 0;
          };
          return score(a.warningSeverity) - score(b.warningSeverity);
      });

      return results;
    });

    // Alias legacy props to empty or links, WITH TYPES
    const trunks = computed<PathDefinition[]>(() => []);
    const freeOutputBranches = computed<PathDefinition[]>(() => []);

    // Sort branches so highlighted ones are drawn LAST (on top of other connections)
    const branches = computed<PathDefinition[]>(() => {
      const allLinks = [...links.value];

      // If no interaction or in selection mode, return default order (Severity sorted)
      if (!hoveredNodeId.value && !hoveredWarningParentId.value && !hoveredRemovableConnection.value) return allLinks;
      if (isSelectionModeActive.value || isBranchOperationActive.value) return allLinks;

      return allLinks.sort((a, b) => {
        const isHighlighted = (item: PathDefinition) => {
           // Check Warning Highlight
           if (item.parentId && hoveredWarningParentId.value === item.parentId) return true;
           // Check Node Hover Highlight
           if (hoveredNodeId.value && item.parentId && isRelatedToHoveredNode(item.parentId)) return true;
           // Check Removable Connection Highlight
           if (hoveredRemovableConnection.value &&
               item.parentId === hoveredRemovableConnection.value.parentId &&
               item.childId === hoveredRemovableConnection.value.childId) return true;
           return false;
        };

        const aHigh = isHighlighted(a);
        const bHigh = isHighlighted(b);

        if (aHigh && !bHigh) return 1; // A (High) > B (Low) -> A comes later
        if (!aHigh && bHigh) return -1;
        return 0;
      });
    });

    // Calculate swap buttons for 2-branch nodes
    const swapButtons = computed<SwapButton[]>(() => {
      const buttons: SwapButton[] = [];
      nodes.value.forEach(node => {
        if (node.outputs === 2) {
           const startY = node.top + node.effectiveHeight;
           const trunkY = startY + VERTICAL_GAP / 2;
           buttons.push({
             stepId: node.step.id,
             x: node.centerX,
             y: trunkY
           });
        }
      });
      return buttons;
    });

    // Calculate removable branch hitareas (for double-click to remove connection)
    // Each branch can have multiple segments (vertical, horizontal, vertical for dogleg paths)
    interface BranchHitarea {
      id: string;
      parentId: string;
      childId: string;
      parentOutput: number;
      x: number;
      y: number;
      width: number;
      height: number;
    }

    const removableBranchHitareas = computed<BranchHitarea[]>(() => {
      if (!props.flow) return [];
      const hitareas: BranchHitarea[] = [];
      const nodesById = new Map(nodes.value.map(n => [n.step.id, n]));
      const hitareaWidth = 20;

      // Find all connections where child has multiple parents
      props.flow.connections.forEach(conn => {
        const parentCount = props.flow!.connections.filter(c => c.childId === conn.childId).length;
        if (parentCount <= 1) return;

        const parentNode = nodesById.get(conn.parentId);
        const childNode = nodesById.get(conn.childId);
        if (!parentNode || !childNode) return;

        // Calculate the path segments matching 'links' computed
        const parentStartY = parentNode.top + parentNode.effectiveHeight;
        const endY = childNode.top;

        if (parentNode.outputs === 1) {
          // Single output case
          const startX = parentNode.centerX;

          if (Math.abs(childNode.centerX - startX) < 2) {
            // Straight vertical line - one segment
            hitareas.push({
              id: `hitarea-${conn.parentId}-${conn.childId}-${conn.parentOutput}-v`,
              parentId: conn.parentId,
              childId: conn.childId,
              parentOutput: conn.parentOutput,
              x: startX - hitareaWidth / 2,
              y: parentStartY,
              width: hitareaWidth,
              height: endY - parentStartY,
            });
          } else {
            // Dogleg: 3 segments - vertical, horizontal, vertical
            const midY = (parentStartY + endY) / 2;

            // Segment 1: Vertical from parent down to midY
            hitareas.push({
              id: `hitarea-${conn.parentId}-${conn.childId}-${conn.parentOutput}-v1`,
              parentId: conn.parentId,
              childId: conn.childId,
              parentOutput: conn.parentOutput,
              x: startX - hitareaWidth / 2,
              y: parentStartY,
              width: hitareaWidth,
              height: midY - parentStartY,
            });

            // Segment 2: Horizontal from parent X to child X at midY
            const minX = Math.min(startX, childNode.centerX);
            const maxX = Math.max(startX, childNode.centerX);
            hitareas.push({
              id: `hitarea-${conn.parentId}-${conn.childId}-${conn.parentOutput}-h`,
              parentId: conn.parentId,
              childId: conn.childId,
              parentOutput: conn.parentOutput,
              x: minX - hitareaWidth / 2,
              y: midY - hitareaWidth / 2,
              width: maxX - minX + hitareaWidth,
              height: hitareaWidth,
            });

            // Segment 3: Vertical from midY down to child
            hitareas.push({
              id: `hitarea-${conn.parentId}-${conn.childId}-${conn.parentOutput}-v2`,
              parentId: conn.parentId,
              childId: conn.childId,
              parentOutput: conn.parentOutput,
              x: childNode.centerX - hitareaWidth / 2,
              y: midY,
              width: hitareaWidth,
              height: endY - midY,
            });
          }
        } else {
          // Multi-output: vertical drop from trunk at child's X position
          const trunkY = parentStartY + VERTICAL_GAP / 2;
          hitareas.push({
            id: `hitarea-${conn.parentId}-${conn.childId}-${conn.parentOutput}-v`,
            parentId: conn.parentId,
            childId: conn.childId,
            parentOutput: conn.parentOutput,
            x: childNode.centerX - hitareaWidth / 2,
            y: trunkY,
            width: hitareaWidth,
            height: endY - trunkY,
          });
        }
      });

      return hitareas;
    });



    // Calculate add button positions for all free outputs (at the end of branches)
    const addButtons = computed<AddButtonPosition[]>(() => {
      const buttons: AddButtonPosition[] = [];
      nodes.value.forEach((node) => {
        // Skip nodes with no outputs (shouldn't happen but safety check)
        if (node.outputs === 0) {
          return;
        }
        // Generate a button for each free output
        for (let i = 1; i <= node.outputs; i++) {
          if (!node.connectedOutputs.includes(i)) {
            // This output is free, calculate position
            let branchEndX = getOutputX(node, i);
            const startY = node.top + node.effectiveHeight;

            let branchEndY: number;
            if (node.outputs === 1) {
              // Single output: button flush against the box bottom
              branchEndX = node.centerX;
              branchEndY = startY;
            } else {
              // Multi output: button at end of shoulder branch stub
              const trunkY = startY + VERTICAL_GAP / 2;
              branchEndY = trunkY + FREE_STUB_LENGTH;
            }

            buttons.push({
              id: `add-${node.step.id}-${i}`,
              parentStepId: node.step.id,
              outputIndex: i,
              top: branchEndY,
              left: branchEndX,
            });
          }
        }
      });
      return buttons;
    });

    // End-of-branch markers (finish flag below add buttons)
    const END_MARKER_OFFSET = 110;
    const endMarkers = computed(() => addButtons.value.map((btn) => ({
      id: `end-${btn.parentStepId}-${btn.outputIndex}`,
      top: btn.top + END_MARKER_OFFSET,
      left: btn.left,
    })));

    // SVG lines connecting add buttons to end markers
    const endMarkerLinks = computed(() => addButtons.value.map((btn) => ({
      id: `end-link-${btn.parentStepId}-${btn.outputIndex}`,
      path: `M ${btn.left} ${btn.top} L ${btn.left} ${btn.top + END_MARKER_OFFSET}`,
    })));

    // Branch warning positions (icons on the incoming branch)


    const branchWarnings = computed<BranchWarningPosition[]>(() => {
      const warnings: BranchWarningPosition[] = [];
      nodes.value.forEach((node) => {
        const branchWarningsList = node.warnings.filter((w) => w.location === 'branch');

        // Get highest severity for this parent's warnings
        const parentConnections = props.flow?.connections.filter((c) => c.childId === node.step.id) || [];

        // Group warnings by parentId
        const warningsByParent = new Map<string, typeof branchWarningsList>();
        branchWarningsList.forEach((w) => {
          if (w.parentId) {
            const existing = warningsByParent.get(w.parentId) || [];
            existing.push(w);
            warningsByParent.set(w.parentId, existing);
          } else {
            // Warning without parentId applies to all parents
            parentConnections.forEach((conn) => {
              const existing = warningsByParent.get(conn.parentId) || [];
              existing.push(w);
              warningsByParent.set(conn.parentId, existing);
            });
          }
        });

        // Create one warning icon per parent with warnings
        warningsByParent.forEach((parentWarnings, parentId) => {
          const parentNode = nodes.value.find((n) => n.step.id === parentId);
          if (!parentNode) return;

          const parentConn = parentConnections.find((c) => c.parentId === parentId);

          // Calculate the position at the middle of the TOTAL line (vertical + horizontal)
          const outputX = parentNode.outputs > 1 && parentConn
            ? getOutputX(parentNode, parentConn.parentOutput)
            : parentNode.centerX;
          const parentBottom = parentNode.top + parentNode.effectiveHeight;

          let x: number;
          let y: number;

          // Calculate position based on the ACTUAL drawn path type
          // Match logic in `links` generation:

          if (parentNode.outputs === 1) {
            // Case A: Single Output (Dogleg or Straight)
            // Path: Start -> MidY -> Horizontal -> Child
            const startX = parentNode.centerX;
            const startY = parentBottom;
            const childX = node.centerX;
            const childY = node.top;

            if (Math.abs(startX - childX) < 2) {
              // Straight Vertical
              x = startX;
              y = (startY + childY) / 2;
            } else {
              // Dogleg (V - H - V)
              const midY = (startY + childY) / 2;
              // Determine where to place the icon.
              // Total length includes V1(startY->midY) + H(startX->childX) + V2(midY->childY).
              // Midpoint is likely on H if H is long, or V if H is short.
              // For simplicity, let's place it on the Horizontal segment if it exists
              x = (startX + childX) / 2;
              y = midY;
            }
          } else {
            // Case B: Multi-Output (Trunk + Drop)
            // Path: Parent -> TrunkY -> Shoulder -> Drop -> Child
            // The "Branch" is effectively the Drop from the Shoulder(at X=ChildX) to the Child.
            // Note: render logic draws the shoulder H-line, but the specific link to THIS child is the vertical drop.
            // We should place warning on that vertical drop.

            // Calculate Drop Start Y (TrunkY)
            const trunkY = parentBottom + VERTICAL_GAP / 2;

            // The drop is vertical at Child.centerX (since connection targets child)
            // Exception: if `outputX` logic in links used `getOutputX`, but for connected nodes it uses `child.centerX`.
            // So Drop X = node.centerX.
            x = node.centerX;
            y = (trunkY + node.top) / 2;
          }

          // Get highest severity for this parent's warnings
          let severity: 'error' | 'warning' | 'info' = 'info';
          if (parentWarnings.some((w) => w.severity === 'error')) {
            severity = 'error';
          } else if (parentWarnings.some((w) => w.severity === 'warning')) {
            severity = 'warning';
          }

          warnings.push({
            id: `branch-warning-${node.step.id}-${parentId}`,
            x,
            y,
            severity,
            messages: parentWarnings.map((w) => w.message),
            parentId,
          });
        });
      });
      return warnings;
    });

    // Output labels for filters with 2 outputs (O = Oui, N = Non)
    const outputLabels = computed<OutputLabelPosition[]>(() => {
      const labels: OutputLabelPosition[] = [];
      const connections = props.flow?.connections || [];
      const nodesMap = new Map(nodes.value.map(n => [n.step.id, n]));

      nodes.value.forEach((node) => {
        // Only show labels for filters with exactly 2 outputs
        const operator = getOperatorById(node.step.type);
        if (!operator || operator.kind !== 'filter' || node.outputs !== 2) {
          return;
        }

        const startY = node.top + node.effectiveHeight;
        const junctionY = startY + VERTICAL_GAP / 2;

        // Helper to determine label position based on destination (child or empty output)
        const getLabelPos = (outputIndex: number): { x: number, y: number } => {
          const startX = node.centerX;
          let endX: number | null = null;
          let isStandardConnection = false;

          // 1. Find the connection to determine geometric type
          const connection = connections.find(c => c.parentId === node.step.id && c.parentOutput === outputIndex);
          if (connection) {
            // Check if this child has multiple parents (Closure) or single parent (Standard)
            const parentCount = connections.filter(c => c.childId === connection.childId).length;

            if (parentCount === 1) {
              // Standard Connection: Drawn horizontally to Child Center -> Elbow is at Child Center
              isStandardConnection = true;
              const childNode = nodes.value.find(n => n.step.id === connection.childId);
              if (childNode) {
                endX = childNode.centerX;
              }
            } else {
              // Closure Connection: Drawn vertically from Output -> Elbow is at Output Position (Shoulder)
              // (The horizontal part is at the bottom, handled by the vertical drop)
              endX = getOutputX(node, outputIndex);
            }
          } else {
            // Empty Output: Elbow is at Output Position (Shoulder)
            endX = getOutputX(node, outputIndex);
          }

          // If valid target found
          if (endX !== null) {
            const segmentLength = Math.abs(endX - startX);
            const offset = 20;

            // Labels should be symmetric ("same offset for O and N") and "before first curvature" (close to trunk).
            // So we position them relative to startX (Trunk) rather than endX (Elbow).
            const labelDist = 60; // Fixed distance from center

            if (isStandardConnection && segmentLength < labelDist) {
              // Very short segment: Center it
              return { x: (startX + endX) / 2, y: junctionY };
            }

            if (endX > startX) {
              // Branch goes Right (Non) -> Place at Trunk + Fixed Dist
              // But don't go past the Elbow (endX)
              return { x: Math.min(startX + labelDist, endX), y: junctionY };
            } else {
              // Branch goes Left (Oui) -> Place at Trunk - Fixed Dist
              // But don't go past the Elbow (endX)
              return { x: Math.max(startX - labelDist, endX), y: junctionY };
            }
          }

          // Fallback (should normally be covered above)
          return { x: getOutputX(node, outputIndex), y: junctionY };
        };

        // Output 1 = O (Oui/Yes) - site green
        const pos1 = getLabelPos(1);
        labels.push({
          id: `output-label-${node.step.id}-1`,
          label: 'O',
          x: pos1.x,
          y: pos1.y,
          color: '#94c840', // site green
        });

        // Output 2 = N (Non/No) - red
        const pos2 = getLabelPos(2);
        labels.push({
          id: `output-label-${node.step.id}-2`,
          label: 'N',
          x: pos2.x,
          y: pos2.y,
          color: '#ef4444', // red
        });
      });
      return labels;
    });

    const updateContainerSize = () => {
      if (!canvasWrapper.value) {
        return;
      }
      containerSize.width = canvasWrapper.value.clientWidth;
      containerSize.height = canvasWrapper.value.clientHeight;
    };

    const fitToScreen = () => {
      nextTick(() => {
        updateContainerSize();
        const widthRatio = containerSize.width / canvasSize.width;
        const heightRatio = containerSize.height / canvasSize.height;
        const newZoom = Math.min(Math.max(Math.min(widthRatio, heightRatio), MIN_ZOOM), MAX_ZOOM);
        zoom.value = newZoom;
        translate.x = (containerSize.width - canvasSize.width * newZoom) / 2;
        translate.y = (containerSize.height - canvasSize.height * newZoom) / 2;
      });
    };

    const focusRoot = () => {
      nextTick(() => {
        updateContainerSize();
        const rootNode = nodes.value.find((node) => node.step.id === props.flow?.rootStepId)
          ?? nodes.value[0];
        if (!rootNode) {
          return;
        }
        zoom.value = 1;
        translate.x = containerSize.width / 2 - rootNode.centerX;
        translate.y = Math.min(40, containerSize.height / 6) - rootNode.top;
      });
    };

    // Center camera on a specific node by stepId
    const focusOnNode = (stepId: string) => {
      nextTick(() => {
        updateContainerSize();
        const node = nodes.value.find((n) => n.step.id === stepId);
        if (!node) {
          return;
        }
        translate.x = containerSize.width / 2 - node.centerX * zoom.value;
        translate.y = containerSize.height / 2 - (node.top + node.effectiveHeight / 2) * zoom.value;
      });
    };

    const clampZoom = (value: number) => Math.min(Math.max(value, MIN_ZOOM), MAX_ZOOM);

    const applyZoom = (newZoom: number, anchorX: number, anchorY: number) => {
      const currentZoom = zoom.value;
      const ratio = newZoom / currentZoom;
      translate.x = anchorX - (anchorX - translate.x) * ratio;
      translate.y = anchorY - (anchorY - translate.y) * ratio;
      zoom.value = newZoom;
    };

    const zoomIn = () => {
      // Round up to next 25% step (e.g., 61% -> 75%)
      const nextStep = Math.ceil(zoom.value / ZOOM_STEP) * ZOOM_STEP;
      const newZoom = clampZoom(nextStep === zoom.value ? zoom.value + ZOOM_STEP : nextStep);
      const anchorX = containerSize.width / 2;
      const anchorY = containerSize.height / 2;
      applyZoom(newZoom, anchorX, anchorY);
    };

    const zoomOut = () => {
      // Round down to previous 25% step (e.g., 61% -> 50%)
      const prevStep = Math.floor(zoom.value / ZOOM_STEP) * ZOOM_STEP;
      const newZoom = clampZoom(prevStep === zoom.value ? zoom.value - ZOOM_STEP : prevStep);
      const anchorX = containerSize.width / 2;
      const anchorY = containerSize.height / 2;
      applyZoom(newZoom, anchorX, anchorY);
    };

    const onWheel = (event: WheelEvent) => {
      const direction = event.deltaY > 0 ? -1 : 1;
      let newZoom: number;
      if (direction > 0) {
        // Zoom in: round up to next 25% step
        const nextStep = Math.ceil(zoom.value / ZOOM_STEP) * ZOOM_STEP;
        newZoom = clampZoom(nextStep === zoom.value ? zoom.value + ZOOM_STEP : nextStep);
      } else {
        // Zoom out: round down to previous 25% step
        const prevStep = Math.floor(zoom.value / ZOOM_STEP) * ZOOM_STEP;
        newZoom = clampZoom(prevStep === zoom.value ? zoom.value - ZOOM_STEP : prevStep);
      }
      const rect = canvasWrapper.value?.getBoundingClientRect();
      const anchorX = rect ? event.clientX - rect.left : containerSize.width / 2;
      const anchorY = rect ? event.clientY - rect.top : containerSize.height / 2;
      applyZoom(newZoom, anchorX, anchorY);
    };

    const onPointerDown = (event: PointerEvent) => {
      // Close branch menu if clicking outside
      if (branchMenuVisible.value) {
        branchMenuVisible.value = false;
      }

      if (event.button !== 0) {
        return;
      }
      isPanning.value = true;
      panStart.x = event.clientX;
      panStart.y = event.clientY;
      panStart.translateX = translate.x;
      panStart.translateY = translate.y;
      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', onPointerUp);
    };

    const onPointerMove = (event: PointerEvent) => {
      if (!isPanning.value) {
        return;
      }
      const deltaX = event.clientX - panStart.x;
      const deltaY = event.clientY - panStart.y;
      translate.x = panStart.translateX + deltaX;
      translate.y = panStart.translateY + deltaY;
    };

    const onPointerUp = () => {
      isPanning.value = false;
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
    };

    const minimapSize = computed(() => {
      const base = 220;
      const ratio = Math.max(canvasSize.width, canvasSize.height);
      if (ratio === 0) {
        return { width: base, height: base };
      }
      const scale = base / ratio;
      return {
        width: canvasSize.width * scale,
        height: canvasSize.height * scale,
      };
    });

    const minimapScale = computed(() => {
      const ratio = Math.max(canvasSize.width, canvasSize.height);
      if (ratio === 0) {
        return 1;
      }
      return minimapSize.value.width / canvasSize.width;
    });

    const visibleRect = computed(() => ({
      left: -translate.x / zoom.value,
      top: -translate.y / zoom.value,
      width: containerSize.width / zoom.value,
      height: containerSize.height / zoom.value,
    }));

    const onMiniMapClick = (event: MouseEvent) => {
      const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
      const x = ((event.clientX - rect.left) / rect.width) * canvasSize.width;
      const y = ((event.clientY - rect.top) / rect.height) * canvasSize.height;
      translate.x = containerSize.width / 2 - x * zoom.value;
      translate.y = containerSize.height / 2 - y * zoom.value;
    };

    // Mini-map collapse state, persisted across sessions.
    // Default = collapsed (pill); the user opts in to the full mini-map.
    const MINIMAP_STORAGE_KEY = 'flowEditor.minimapCollapsed';
    const minimapCollapsed = ref(true);
    try {
      const stored = window.localStorage?.getItem(MINIMAP_STORAGE_KEY);
      if (stored !== null && stored !== undefined) {
        minimapCollapsed.value = stored !== '0';
      }
    } catch {
      // localStorage unavailable (private mode, SSR) — ignore, keep default collapsed
    }
    const setMinimapCollapsed = (value: boolean) => {
      minimapCollapsed.value = value;
      try {
        window.localStorage?.setItem(MINIMAP_STORAGE_KEY, value ? '1' : '0');
      } catch {
        // ignore persistence failure
      }
    };

    watch(() => props.flow, (flow) => {
      if (!flow) {
        return;
      }
      nextTick(() => {
        updateContainerSize();
        focusRoot();
      });
    }, { immediate: true });

    onMounted(() => {
      updateContainerSize();
      window.addEventListener('resize', updateContainerSize);
      if (nodes.value.length) {
        focusRoot();
      }
    });

    onBeforeUnmount(() => {
      // Reset selection mode when component unmounts (e.g., switching scenarios)
      if (isSelectionModeActive.value) {
        forceResetSelection();
      }
      window.removeEventListener('resize', updateContainerSize);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
    });

    // Operator panel state
    const operatorPanelVisible = ref(false);
    const operatorPanelContext = ref<OperatorInsertContext | null>(null);
    const pendingInsertNode = ref<LayoutNode | null>(null);
    const pendingFocusStepId = ref<string | null>(null);

    const getCampaignType = () => {
      return props.type;
    };

    // Get all ancestor operator types for a given step (for dependency checking)
    const getAllAncestorTypes = (stepId: string): string[] => {
      if (!props.flow) return [];
      const ancestorTypes: string[] = [];
      const visited = new Set<string>();

      const traverse = (currentId: string) => {
        if (visited.has(currentId)) return;
        visited.add(currentId);

        const step = props.flow?.steps.find(s => s.id === currentId);
        if (!step) return;

        // Add this step's type to ancestors
        ancestorTypes.push(step.type);

        // Traverse all parents
        for (const parent of step.parents) {
          traverse(parent.id);
        }
      };

      // Start from the step's parents (not the step itself)
      const step = props.flow.steps.find(s => s.id === stepId);
      if (step) {
        for (const parent of step.parents) {
          traverse(parent.id);
        }
      }

      return ancestorTypes;
    };

    // Get all ancestor step IDs for a given step (for linked operator selection)
    const getAllAncestorStepIds = (stepId: string): string[] => {
      if (!props.flow) return [];
      const ancestorIds: string[] = [];
      const visited = new Set<string>();

      const traverse = (currentId: string) => {
        if (visited.has(currentId)) return;
        visited.add(currentId);

        const step = props.flow?.steps.find(s => s.id === currentId);
        if (!step) return;

        // Add this step's ID to ancestors
        ancestorIds.push(step.id);

        // Traverse all parents
        for (const parent of step.parents) {
          traverse(parent.id);
        }
      };

      // Start from the step's parents (not the step itself)
      const step = props.flow.steps.find(s => s.id === stepId);
      if (step) {
        for (const parent of step.parents) {
          traverse(parent.id);
        }
      }

      return ancestorIds;
    };

    const onAddBefore = (node: LayoutNode) => {
      pendingInsertNode.value = node;
      const parentId = node.step.primaryParent;
      // Get the parent's operator type (not step id)
      const parentStep = parentId ? props.flow?.steps.find(s => s.id === parentId.id) : null;
      // Get all ancestor types for dependency checking
      const allAncestorTypes = getAllAncestorTypes(node.step.id);
      // Get all ancestor step IDs for linked operator selection
      const allAncestorStepIds = getAllAncestorStepIds(node.step.id);
      operatorPanelContext.value = {
        campaignType: getCampaignType(),
        isRoot: false,
        insertPosition: 'before',
        parentOperatorId: parentStep?.type,
        parentOperatorData: parentStep ? parseOperatorParameters(parentStep.parameters) : undefined,
        childOperatorId: node.step.type,
        allAncestorTypes,
        allAncestorStepIds,
      };
      operatorPanelVisible.value = true;
    };

    const onAddAfter = (node: LayoutNode, outputIndex?: number) => {
      pendingInsertNode.value = node;
      // Get all ancestor types including the current node
      const allAncestorTypes = [node.step.type, ...getAllAncestorTypes(node.step.id)];
      // Get all ancestor step IDs including the current node
      const allAncestorStepIds = [node.step.id, ...getAllAncestorStepIds(node.step.id)];
      operatorPanelContext.value = {
        campaignType: getCampaignType(),
        isRoot: false,
        insertPosition: 'after',
        parentOperatorId: node.step.type,
        parentOperatorData: parseOperatorParameters(node.step.parameters),
        parentStepId: node.step.id,
        outputIndex: outputIndex ?? 1,
        allAncestorTypes,
        allAncestorStepIds,
        flow: props.flow ?? undefined,
      };
      operatorPanelVisible.value = true;
    };

    // Branch menu state
    const branchMenuVisible = ref(false);
    const branchMenuPosition = ref({ x: 0, y: 0 });
    const branchMenuStepId = ref<string | null>(null);
    const branchMenuOutputIndex = ref<number>(1);
    const branchMenuShowMerge = ref(false);
    const branchMenuShowClosure = ref(false);

    // Initialize branch operations composable
    const flowRef = computed(() => props.flow);
    const branchOps = useBranchOperations(flowRef);

    // Check if merge/closure options should be available for a step
    const canShowBranchOptionsForStep = (stepId: string, output: number): { merge: boolean; closure: boolean } => {
      if (!props.flow) return { merge: false, closure: false };

      // Check if this output is free (not connected)
      const isOutputFree = !props.flow.connections.some(
        c => c.parentId === stepId && c.parentOutput === output
      );
      if (!isOutputFree) return { merge: false, closure: false };

      const currentStep = props.flow.steps.find(s => s.id === stepId);
      if (!currentStep) return { merge: false, closure: false };

      // Get operator metadata
      const operator = getOperatorById(currentStep.type);
      const outputsDef = operator?.outputs ?? 1;
      const totalOutputs: number = typeof outputsDef === 'function'
        ? outputsDef(parseOperatorParameters(currentStep.parameters))
        : outputsDef;

      // Check if this step has multiple outputs
      const isMultiOutputNode = totalOutputs > 1;

      // Check if this step has a parent with multiple outputs
      const hasParentWithMultiOutputs = (): boolean => {
        const ancestors = branchOps.getAncestors(stepId);
        for (const ancestorId of ancestors) {
          const ancestorStep = props.flow?.steps.find(s => s.id === ancestorId);
          if (!ancestorStep) continue;
          const ancestorOp = getOperatorById(ancestorStep.type);
          const ancestorOutputsDef = ancestorOp?.outputs ?? 1;
          const ancestorTotalOutputs: number = typeof ancestorOutputsDef === 'function'
            ? ancestorOutputsDef(parseOperatorParameters(ancestorStep.parameters))
            : ancestorOutputsDef;
          if (ancestorTotalOutputs > 1) return true;
        }
        return false;
      };

      // Count "conditioned boxes" - boxes that can be closed/merged
      // A box is "conditioned" if: (!hasMultipleOutputs OR isFilter) AND !allOutputsLinked AND hasParentWithMultiOutputs
      let nbConditionedBoxes = 0;
      props.flow.steps.forEach(step => {
        const stepOp = getOperatorById(step.type);
        const stepOutputsDef = stepOp?.outputs ?? 1;
        const stepTotalOutputs: number = typeof stepOutputsDef === 'function'
          ? stepOutputsDef(parseOperatorParameters(step.parameters))
          : stepOutputsDef;
        const stepIsMultiOutput = stepTotalOutputs > 1;
        const stepIsFilter = stepOp?.kind === 'filter';

        // Check if all outputs are linked
        const connectedOutputs = props.flow?.connections
          .filter(c => c.parentId === step.id)
          .map(c => c.parentOutput) || [];
        const allOutputsLinked = connectedOutputs.length >= stepTotalOutputs;

        // Check if has parent with multi outputs
        const stepAncestors = branchOps.getAncestors(step.id);
        let stepHasParentWithMultiOutputs = false;
        for (const ancestorId of stepAncestors) {
          const ancestorStep = props.flow?.steps.find(s => s.id === ancestorId);
          if (!ancestorStep) continue;
          const ancestorOp = getOperatorById(ancestorStep.type);
          const ancestorOutputsDef = ancestorOp?.outputs ?? 1;
          const ancestorTotalOutputs: number = typeof ancestorOutputsDef === 'function'
            ? ancestorOutputsDef(parseOperatorParameters(ancestorStep.parameters))
            : ancestorOutputsDef;
          if (ancestorTotalOutputs > 1) {
            stepHasParentWithMultiOutputs = true;
            break;
          }
        }

        if ((!stepIsMultiOutput || stepIsFilter) && !allOutputsLinked && stepHasParentWithMultiOutputs) {
          nbConditionedBoxes++;
        }
      });

      // Show popup only if:
      // 1. nbConditionedBoxes > 1
      // 2. AND ((!isMultiOutput && hasParentWithMultiOutputs) OR isFilter)
      // 3. AND not all outputs are linked
      const isFilter = operator?.kind === 'filter';
      const connectedOutputs = props.flow.connections
        .filter(c => c.parentId === stepId)
        .map(c => c.parentOutput);
      const allOutputsLinked = connectedOutputs.length >= totalOutputs;

      const shouldShowPopup = nbConditionedBoxes > 1
        && ((!isMultiOutputNode && hasParentWithMultiOutputs()) || isFilter)
        && !allOutputsLinked;

      if (!shouldShowPopup) {
        return { merge: false, closure: false };
      }

      // If we should show popup, both options are always available
      return { merge: true, closure: true };
    };

    const onAddButtonClick = (btn: AddButtonPosition) => {
      const node = nodes.value.find((n) => n.step.id === btn.parentStepId);
      if (!node) return;

      // In closure mode, toggle output selection
      if (branchOps.selectionState.value.mode === 'closure-select') {
        if (isClosureOutputSelectable(btn.parentStepId, btn.outputIndex) ||
          branchOps.isOutputSelectedForClosure(btn.parentStepId, btn.outputIndex)) {
          branchOps.toggleClosureOutput(btn.parentStepId, btn.outputIndex);
        }
        return;
      }

      // Check if we should show the branch menu or directly add
      const options = canShowBranchOptionsForStep(btn.parentStepId, btn.outputIndex);

      if (options.merge || options.closure) {
        // Show branch menu
        branchMenuStepId.value = btn.parentStepId;
        branchMenuOutputIndex.value = btn.outputIndex;
        branchMenuShowMerge.value = options.merge;
        branchMenuShowClosure.value = options.closure;
        branchMenuPosition.value = { x: btn.left, y: btn.top + 20 };
        branchMenuVisible.value = true;
      } else {
        // Directly add operator
        onAddAfter(node, btn.outputIndex);
      }
    };

    const onBranchMenuAction = (action: BranchMenuAction) => {
      branchMenuVisible.value = false;

      const node = nodes.value.find((n) => n.step.id === branchMenuStepId.value);
      if (!node) return;

      if (action === 'add') {
        onAddAfter(node, branchMenuOutputIndex.value);
      } else if (action === 'merge') {
        branchOps.startMerge(branchMenuStepId.value!, branchMenuOutputIndex.value);
      } else if (action === 'closure') {
        branchOps.startClosure(branchMenuStepId.value!, branchMenuOutputIndex.value);
      }
    };

    const closeBranchMenu = () => {
      branchMenuVisible.value = false;
    };

    // Handle clicks outside the menu to close it
    const onCanvasClick = () => {
      if (branchMenuVisible.value) {
        closeBranchMenu();
      }
    };

    // Branch operation helpers
    const isBranchOperationActive = computed(() => {
      return branchOps.selectionState.value.mode !== 'normal';
    });

    const isMergeTargetSelectable = (stepId: string): boolean => {
      if (branchOps.selectionState.value.mode !== 'merge-select') return false;
      return branchOps.selectionState.value.availableTargets.includes(stepId);
    };

    const isMergeTargetSelected = (stepId: string): boolean => {
      if (branchOps.selectionState.value.mode !== 'merge-select') return false;
      return branchOps.selectionState.value.mergeData?.targetStepId === stepId;
    };

    // Check if an output is selectable for closure
    const isClosureOutputSelectable = (stepId: string, output: number): boolean => {
      if (branchOps.selectionState.value.mode !== 'closure-select') return false;
      return branchOps.selectionState.value.availableTargets.includes(`${stepId}:${output}`);
    };

    const onNodeSelectionToggle = (stepId: string) => {
      // Handle linked operator selection mode
      if (isSelectionModeActive.value) {
        toggleStepSelection(stepId);
        return;
      }

      // Handle merge mode - select target
      if (branchOps.selectionState.value.mode === 'merge-select') {
        if (isMergeTargetSelectable(stepId)) {
          branchOps.selectMergeTarget(stepId);
        }
        return;
      }
    };

    // Validate merge operation - emit event to parent
    const onMergeValidate = () => {
      const mergeData = branchOps.getMergeData();
      if (mergeData && mergeData.targetStepId) {
        emit('merge-branch', {
          sourceStepId: mergeData.sourceStepId,
          sourceOutput: mergeData.sourceOutput,
          targetStepId: mergeData.targetStepId,
        });
        branchOps.resetSelection();
      }
    };

    // Validate closure operation - open operator panel with closure context
    const onClosureValidate = () => {
      const closureData = branchOps.getClosureData();
      if (closureData && closureData.selectedOutputs.length >= 2) {
        // Collect direct parent types (the steps whose outputs are selected)
        const directParentTypes = new Set<string>();
        // Collect all ancestor types from all selected outputs (for dependency checking)
        const allAncestorTypes = new Set<string>();
        const allAncestorStepIds = new Set<string>();

        closureData.selectedOutputs.forEach(output => {
          // Add the step itself as direct parent
          const step = props.flow?.steps.find(s => s.id === output.stepId);
          if (step) {
            directParentTypes.add(step.type);
            allAncestorTypes.add(step.type);
            allAncestorStepIds.add(step.id);
          }

          // Add all ancestors (for dependency checking only)
          const ancestors = branchOps.getAncestors(output.stepId);
          ancestors.forEach(ancestorId => {
            allAncestorStepIds.add(ancestorId);
            const ancestorStep = props.flow?.steps.find(s => s.id === ancestorId);
            if (ancestorStep) {
              allAncestorTypes.add(ancestorStep.type);
            }
          });
        });

        // Store closure data and open operator panel
        // The operator panel will use this data when adding the operator
        operatorPanelContext.value = {
          campaignType: props.type,
          isRoot: false,
          insertPosition: 'closure',
          closureOutputs: closureData.selectedOutputs,
          directParentTypes: Array.from(directParentTypes),
          allAncestorTypes: Array.from(allAncestorTypes),
          allAncestorStepIds: Array.from(allAncestorStepIds),
          flow: props.flow ?? undefined,
        };
        operatorPanelVisible.value = true;
        branchOps.resetSelection();
      }
    };

    // Track step IDs before add to detect new step
    const stepIdsBeforeAdd = ref<Set<string> | null>(null);

    const onOperatorAdd = (payload: { operator: FlowOperatorMetadata; data: any }) => {
      // Store current step IDs to detect the new one after flow update
      stepIdsBeforeAdd.value = new Set(props.flow?.steps.map((s) => s.id) ?? []);
      emit('add-operator', {
        operator: payload.operator,
        data: payload.data,
        context: operatorPanelContext.value,
        targetStepId: pendingInsertNode.value?.step.id,
      });
      pendingInsertNode.value = null;
    };

    // Edit state
    const selectedStep = ref<FlowStructureDto['steps'][number] | null>(null);

    const onNodeClick = (node: LayoutNode) => {
      // In merge mode, clicking a selectable node selects it as target
      if (branchOps.selectionState.value.mode === 'merge-select') {
        if (isMergeTargetSelectable(node.step.id)) {
          branchOps.selectMergeTarget(node.step.id);
        }
        return;
      }

      // In closure mode, ignore clicks on nodes
      if (branchOps.selectionState.value.mode === 'closure-select') {
        return;
      }

      // Normal mode - open edit panel
      // Reset first to ensure watch triggers even if clicking same node
      selectedStep.value = null;
      operatorPanelContext.value = null;
      // Use nextTick to ensure the reset is processed before setting new value
      nextTick(() => {
        selectedStep.value = node.step;
        operatorPanelVisible.value = true;
      });
    };

    const onStepSave = (payload: { stepId: string; data: any }) => {
      pendingFocusStepId.value = payload.stepId;
      emit('update-step', payload);
      selectedStep.value = null;

      // Force revalidation of only the saved step's warnings after a short delay
      // to ensure the flow state has been updated
      nextTick(() => {
        const flow = props.flow;
        if (!flow) return;
        const step = flow.steps.find((s) => s.id === payload.stepId);
        if (step) {
          validatedStepSignatures.value.delete(payload.stepId);
          validateStep(step, flow, props.type);
          // Update signature after validation
          const signature = getStepSignature(step);
          validatedStepSignatures.value.set(step.id, signature);
        }
      });
    };

    const onDecreaseBranches = (payload: { stepId: string; outputsToRemove: string[] }) => {
      emit('decrease-branches', payload);
    };

    const onNodeDelete = (node: LayoutNode) => {
      emit('delete-step', { stepId: node.step.id });
    };

    // Replace state
    const replaceStep = ref<FlowStructureDto['steps'][number] | null>(null);

    const onMenuAction = (actionId: string, step: FlowStructureDto['steps'][number]) => {
      if (actionId === 'reorder') {
        reorderStepId.value = step.id;
        reorderModalVisible.value = true;
        return;
      }
      emit('menu-action', actionId, step);
    };

    // Branch Reorder Logic
    const reorderModalVisible = ref(false);
    const reorderStepId = ref<string | null>(null);

    const onReorderSave = (payload: { stepId: string; newOrder: number[] }) => {
      emit('reorder-branches', payload);
    };

    const onSwapBranches = (stepId: string) => {
      emit('reorder-branches', { stepId, newOrder: [2, 1] });
    };

    const onHitareaDoubleClick = (hitarea: { parentId: string; childId: string; parentOutput: number }) => {
      // Don't allow in selection or branch operation modes
      if (isSelectionModeActive.value || isBranchOperationActive.value) {
        return;
      }

      // Clear hover state before deletion
      hoveredRemovableConnection.value = null;

      emit('remove-connection', {
        parentId: hitarea.parentId,
        parentOutput: hitarea.parentOutput,
        childId: hitarea.childId,
      });
    };

    // Handle enabled operator toggle
    const onToggleEnabled = (payload: { stepId: string; enabled: number; systemAutoDisabled: boolean }) => {
      emit('update-step', {
        stepId: payload.stepId,
        data: {
          enabled_operator: payload.enabled,
          system_auto_disabled: payload.systemAutoDisabled ? '1' : '0',
        },
      });
    };

    const onNodeReplace = (node: LayoutNode) => {
      replaceStep.value = node.step;
      // Open panel with same context as adding at this position
      const parentId = node.step.primaryParent;
      // Get the parent's operator type (not step id)
      const parentStep = parentId ? props.flow?.steps.find(s => s.id === parentId.id) : null;
      // Get the first child's operator type (not step id)
      const firstChild = node.step.children[0];
      const childStep = firstChild ? props.flow?.steps.find(s => s.id === firstChild.id) : null;
      // Get all ancestor types for dependency checking (include ancestors of the step being replaced)
      const allAncestorTypes = getAllAncestorTypes(node.step.id);
      // Get all ancestor step IDs for linked operator selection
      const allAncestorStepIds = getAllAncestorStepIds(node.step.id);

      operatorPanelContext.value = {
        campaignType: getCampaignType(),
        isRoot: !parentId,
        insertPosition: 'replace',
        parentOperatorId: parentStep?.type,
        parentOperatorData: parentStep ? parseOperatorParameters(parentStep.parameters) : undefined,
        childOperatorId: childStep?.type,
        allAncestorTypes,
        allAncestorStepIds,
      };
      operatorPanelVisible.value = true;
    };

    const onOperatorReplace = (payload: { operator: FlowOperatorMetadata; data: any }) => {
      if (!replaceStep.value) return;
      pendingFocusStepId.value = replaceStep.value.id;
      emit('replace-step', {
        operator: payload.operator,
        data: payload.data,
        stepId: replaceStep.value.id,
      });
      replaceStep.value = null;
    };

    // Handle empty state start button click
    const onEmptyStateStart = (type: FlowStructureType) => {
      emit('start-flow', type);
    };

    // Open trigger panel (for automation - shows all triggers)
    const openTriggerPanel = () => {
      operatorPanelContext.value = {
        campaignType: getCampaignType(),
        isRoot: true,
        insertPosition: 'after',
      };
      operatorPanelVisible.value = true;
    };

    // Open trigger form directly (for bulk - opens AudienceSetup form)
    const openTriggerForm = (operatorId: string) => {
      const operator = getOperatorById(operatorId);
      if (!operator) return;

      operatorPanelContext.value = {
        campaignType: getCampaignType(),
        isRoot: true,
        insertPosition: 'after',
        preSelectedOperator: operator,
      };
      operatorPanelVisible.value = true;
    };

    // Reset state when panel closes
    watch(operatorPanelVisible, (visible) => {
      if (!visible) {
        selectedStep.value = null;
        replaceStep.value = null;
      }
    });

    // Focus on step after flow update (for edit/replace/add)
    watch(() => props.flow?.steps, (newSteps) => {
      if (!newSteps) return;

      // Check for new step after add
      if (stepIdsBeforeAdd.value) {
        const newStep = newSteps.find((s) => !stepIdsBeforeAdd.value!.has(s.id));
        if (newStep) {
          stepIdsBeforeAdd.value = null;
          setTimeout(() => focusOnNode(newStep.id), 100);
          return;
        }
      }

      // Check for pending focus (edit/replace)
      if (pendingFocusStepId.value) {
        const stepId = pendingFocusStepId.value;
        pendingFocusStepId.value = null;
        setTimeout(() => focusOnNode(stepId), 100);
      }
    }, { deep: true });

    return {
      BOX_WIDTH,
      BOX_HEIGHT,
      canvasWrapper,
      nodes,
      canvasSize,
      canvasTransform,
      zoom,
      zoomIn,
      zoomOut,
      fitToScreen,
      trunks,
      branches,
      swapButtons,
      freeOutputBranches,
      branchWarnings,
      hoveredWarningParentId,
      hoveredNodeId,
      hoveredRemovableConnection,
      outputLabels,
      t,
      addButtons,
      endMarkers,
      endMarkerLinks,
      onAddButtonClick,
      minimapSize,
      minimapScale,
      minimapCollapsed,
      setMinimapCollapsed,
      visibleRect,
      onMiniMapClick,
      onPointerDown,
      onWheel,
      emptyLabel: computed(() => props.emptyLabel),
      operatorPanelVisible,
      operatorPanelContext,
      onAddBefore,
      onAddAfter,
      onOperatorAdd,
      selectedStep,
      onNodeClick,
      onStepSave,
      onPanelUpdateStep,
      onDecreaseBranches,
      onNodeDelete,
      onNodeReplace,
      onMenuAction,
      handleWarningAction,
      loadingStepIds,
      onToggleEnabled,
      replaceStep,
      onOperatorReplace,
      reorderModalVisible,
      reorderStepId,
      onReorderSave,
      onSwapBranches,
      removableBranchHitareas,
      onHitareaDoubleClick,
      // Target bulk calculations
      getTargetCalculation,
      targetCalculations,
      // Empty state handler
      onEmptyStateStart,
      // Exposed methods for parent component
      openTriggerPanel,
      openTriggerForm,
      // Check for blocking warnings
      getBlockingWarnings: () => {
        const blockingWarnings: { stepId: string; warnings: NodeWarning[] }[] = [];
        nodeWarnings.value.forEach((warnings, stepId) => {
          const blocking = warnings.filter(w => w.blocking);
          if (blocking.length > 0) {
            blockingWarnings.push({ stepId, warnings: blocking });
          }
        });
        return blockingWarnings;
      },
      hasBlockingWarnings: () => {
        for (const warnings of nodeWarnings.value.values()) {
          if (warnings.some(w => w.blocking)) {
            return true;
          }
        }
        return false;
      },
      // Linked operator selection mode
      isSelectionModeActive,
      isStepSelectable,
      isStepSelected,
      toggleStepSelection,
      confirmSelection,
      cancelSelection,
      // Linked operator highlight (hover)
      isStepHighlighted,
      onNodeMouseEnter,
      onNodeMouseLeave,
      isRelatedToHoveredNode,
      // Branch menu
      branchMenuVisible,
      branchMenuPosition,
      branchMenuShowMerge,
      branchMenuShowClosure,
      onBranchMenuAction,
      closeBranchMenu,
      // Branch operations state
      branchOps,
      onMergeValidate,
      onClosureValidate,
      isBranchOperationActive,
      isMergeTargetSelectable,
      isMergeTargetSelected,
      isClosureOutputSelectable,
      onNodeSelectionToggle,
      // Analytics mode
      flowAnalytics,
      isStepTraversed,
      handleNodeResize,
    };
  },
});
</script>

<style lang="scss" scoped>
.flow-renderer {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #f6f7fb;

  &__canvas-wrapper {
    position: relative;
    flex: 1;
    overflow: hidden;
    border-radius: 1rem;
    background-image: radial-gradient(#d9e0ec 1px, transparent 0);
    background-size: 24px 24px;
    background-position: -12px -12px;
    cursor: grab;
  }

  &__canvas-wrapper:active {
    cursor: grabbing;
  }

  &__canvas {
    position: absolute;
    top: 0;
    left: 0;
    transform-origin: 0 0;
  }

  &__connections {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;

    path {
      fill: none;
      stroke: #cfd6e3;
      stroke-width: 3;
      stroke-linecap: round;
    }
  }

  &__connection-branch--error {
    stroke: #f87171 !important;
  }

  &__connection-branch--warning {
    stroke: #fbbf24 !important;
  }

  &__connection-branch--info {
    stroke: #60a5fa !important;
  }

  &__connection-branch--hovered {
    stroke-width: 4px !important;
    filter: brightness(0.7);
  }

  &__branch-hitarea {
    position: absolute;
    cursor: pointer;
    z-index: 5;
  }

  &__connection-branch--removable {
    cursor: pointer;
    pointer-events: stroke;

    &:hover {
      stroke: #ef4444 !important;
      stroke-width: 4px !important;
    }
  }

  &__nodes {
    position: absolute;
    top: 0;
    left: 0;
  }

  &__empty {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #6b7585;
    font-size: 1rem;
  }

  &__minimap {
    position: absolute;
    right: 2rem;
    bottom: 2rem;
    width: auto;
    padding: 0.75rem;
    background: rgba(255, 255, 255, 0.95);
    border-radius: 0.75rem;
    box-shadow: 0 12px 24px rgba(15, 23, 42, 0.12);

    svg {
      display: block;
    }
  }

  &__minimap-svg {
    cursor: pointer;
  }

  // Close badge: ~40% outside / 60% inside the top-right corner of the
  // minimap card, anchored visually like a notification dot.
  &__minimap-collapse {
    position: absolute;
    top: 0;
    right: 0;
    transform: translate(40%, -40%);
    width: 1.5rem;
    height: 1.5rem;
    padding: 0;
    border: 1px solid rgba(15, 23, 42, 0.1);
    border-radius: 50%;
    background: #fff;
    color: #475569;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 0.7rem;
    line-height: 1;
    transition: background 0.15s ease, color 0.15s ease, transform 0.15s ease;
    z-index: 1;

    &:hover {
      background: #f8fafc;
      color: #0f172a;
      transform: translate(40%, -40%) scale(1.1);
    }
  }

  &__minimap-pill {
    position: absolute;
    right: 2rem;
    bottom: 2rem;
    width: 2.25rem;
    height: 2.25rem;
    padding: 0;
    border: none;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.95);
    color: #475569;
    box-shadow: 0 8px 18px rgba(15, 23, 42, 0.12);
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 0.95rem;
    transition: background 0.15s ease, color 0.15s ease, transform 0.15s ease;

    &:hover {
      background: #fff;
      color: #0f172a;
      transform: scale(1.05);
    }
  }

  &__minimap-fade-enter-active,
  &__minimap-fade-leave-active {
    transition: opacity 0.18s ease, transform 0.18s ease;
  }

  &__minimap-fade-enter-from,
  &__minimap-fade-leave-to {
    opacity: 0;
    transform: scale(0.85);
  }

  &__minimap-node {
    fill: rgba(121, 134, 203, 0.35);
    stroke: rgba(121, 134, 203, 0.65);
    stroke-width: 1;

    &--trigger {
      fill: rgba(255, 163, 26, 0.25);
      stroke: rgba(255, 163, 26, 0.55);
    }

    &--action {
      fill: rgba(144, 213, 28, 0.25);
      stroke: rgba(144, 213, 28, 0.55);
    }

    &--filter {
      fill: rgba(145, 196, 242, 0.25);
      stroke: rgba(145, 196, 242, 0.55);
    }
  }

  &__minimap-viewport {
    fill: rgba(52, 71, 103, 0.12);
  }

  // Branch warning styles
  &__connection-branch--warning {
    stroke: #f59e0b !important;
    stroke-width: 3;
  }

  &__branch-warning {
    position: absolute;
    transform: translate(-50%, -50%);
    width: 24px;
    height: 24px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: help;
    z-index: 15;
    background: #fff;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);

    i {
      font-size: 0.75rem;
    }

    &--error {
      border: 2px solid #dc2626;
      color: #dc2626;
    }

    &--warning {
      border: 2px solid #f59e0b;
      color: #f59e0b;
    }

    &--info {
      border: 2px solid #3b82f6;
      color: #3b82f6;
    }

    &--dimmed {
      opacity: 0.3;
      pointer-events: none;
    }

    &:hover &-tooltip {
      opacity: 1;
      visibility: visible;
      transform: translateX(-50%) translateY(0);
    }
  }

  &__branch-warning-tooltip {
    position: absolute;
    bottom: calc(100% + 8px);
    left: 50%;
    transform: translateX(-50%) translateY(4px);
    background: #1f2937;
    color: #fff;
    padding: 0.5rem 0.75rem;
    border-radius: 0.5rem;
    font-size: 0.75rem;
    font-weight: 500;
    white-space: nowrap;
    opacity: 0;
    visibility: hidden;
    transition: opacity 0.15s ease, transform 0.15s ease, visibility 0.15s ease;
    z-index: 100;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);

    span {
      display: block;

      &::before {
        content: "- ";
      }
    }

    &--single span::before {
      content: none;
    }

    &::after {
      content: "";
      position: absolute;
      top: 100%;
      left: 50%;
      transform: translateX(-50%);
      border: 6px solid transparent;
      border-top-color: #1f2937;
    }
  }

  &__end-marker-links path {
    fill: none;
    stroke: #cfd6e3;
    stroke-width: 2;
    stroke-linecap: round;
    stroke-dasharray: 4 3;
  }

  &__end-marker {
    position: absolute;
    transform: translate(-50%, -50%);
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: #f3f4f6;
    border: 1.5px solid #d1d5db;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 8;
    cursor: default;

    i {
      font-size: 0.75rem;
      color: #9ca3af;
    }

    &:hover &-tooltip {
      opacity: 1;
      visibility: visible;
      transform: translateX(-50%) translateY(0);
    }
  }

  &__end-marker-tooltip {
    position: absolute;
    bottom: calc(100% + 6px);
    left: 50%;
    transform: translateX(-50%) translateY(4px);
    background: #1f2937;
    color: #fff;
    padding: 0.3rem 0.6rem;
    border-radius: 0.4rem;
    font-size: 0.7rem;
    font-weight: 500;
    white-space: nowrap;
    opacity: 0;
    visibility: hidden;
    transition: opacity 0.15s ease, transform 0.15s ease, visibility 0.15s ease;
    z-index: 100;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);

    &::after {
      content: "";
      position: absolute;
      top: 100%;
      left: 50%;
      transform: translateX(-50%);
      border: 5px solid transparent;
      border-top-color: #1f2937;
    }
  }

  &__output-label {
    position: absolute;
    transform: translate(-50%, -50%);
    width: 18px;
    height: 18px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    font-weight: 700;
    background-color: white;
    border: 2px solid currentColor;
    z-index: 10;
    pointer-events: none;

    &--dimmed {
      opacity: 0.3;
    }
  }

  &__swap-button {
    position: absolute;
    transform: translate(-50%, -50%);
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: white;
    border: 1px solid #d1d5db;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    z-index: 20;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
    transition: all 0.2s ease;
    color: #6b7280;

    &:hover {
      border-color: #9ca3af;
      transform: translate(-50%, -50%) scale(1.1);
      color: #374151;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }

    i {
      font-size: 0.75rem;
    }
  }

  &__selection-bar {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 1000;
    background: white;
    border-top: 1px solid #e5e7eb;
    box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.08);
    padding: 1rem 2rem;
  }

  &__selection-bar-content {
    max-width: 1200px;
    margin: 0 auto;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1.5rem;
  }

  &__selection-bar-text {
    font-weight: 500;
    color: #374151;
    font-size: 0.9375rem;
    display: flex;
    align-items: center;
    gap: 0.625rem;

    &::before {
      content: "\f245";
      font-family: "Font Awesome 6 Pro";
      font-weight: 400;
      font-size: 1rem;
      color: #6b7280;
    }
  }

  &__selection-bar-actions {
    display: flex;
    gap: 0.75rem;

    .p-button-text {
      color: #6b7280 !important;
      border: 1px solid #d1d5db !important;
      background: white !important;
      padding: 0.625rem 1.25rem;

      &:hover {
        background: #f9fafb !important;
        border-color: #9ca3af !important;
        color: #374151 !important;
      }
    }

    .p-button-success {
      background: #94c840 !important;
      color: white !important;
      border: none !important;
      font-weight: 500;
      padding: 0.625rem 1.25rem;

      &:hover {
        background: #7fb835 !important;
      }
    }
  }
}

// Selection bar slide animation
.slide-up-enter-active,
.slide-up-leave-active {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.slide-up-enter-from,
.slide-up-leave-to {
  transform: translateY(100%);
  opacity: 0;
}
</style>
